/**
 * Whatsapp Tool Contracts
 *
 * Source-of-truth `command -> { Args, Result }` mapping for the engine
 * dispatch boundary (B14). Both ends consume this:
 *
 *   - Backend `callEngineTool<Args, Result>(...)` in api/v1/lib/engine-dispatch.ts
 *   - Extension `registerToolHandler<Args>(cmd, fn): Promise<Result>` in
 *     extension/context/page/services/PageMessageService.ts
 *
 * Migration to zod schemas (2026-05-22):
 *   - Every Args/Result is now `z.infer<typeof XSchema>` rather than a
 *     hand-written interface. The schemas are the runtime + type SSoT.
 *   - Backend can `XSchema.parse(resp)` at the dispatch boundary to
 *     catch engine-side regressions before they surface as `undefined`
 *     reads downstream.
 *   - Extension imports the same schemas (zod is now a regular dep on
 *     both sides) so a tightened field on the contract breaks one side's
 *     tsc before it can drift the other.
 *
 * Adding a new command:
 *   1. Add the constant to whatsapp-tools-commands.interface.ts
 *   2. Add an Args + Result schema below (zod), plus an entry in
 *      `WaToolContractMap` keyed by the command literal.
 *   3. Both backend caller and extension handler now see the typed shape
 *      AND get runtime validation at the boundary if they opt into it.
 *
 * Schemas are STRICT — no `.passthrough()`. Unknown engine fields trip
 * parse, which is the source-of-truth signal for engine drift (per
 * "no ambiguous shapes" rule). When the engine adds a field we want
 * to consume, model it here first.
 */

import { z } from 'zod';
import { WA_TOOL_COMMANDS } from './whatsapp-tools-commands.interface';
import { BtSlimMessageSchema } from '../schemas/wa/message';
import { isMessageTypeFilterName } from '../schemas/wa/message-type-map';
import { BtSlimChatSchema } from '../schemas/wa/chat';
import { BtSlimParticipantSchema } from '../schemas/wa/participant';
import { BtSlimGroupSchema } from '../schemas/wa/group';
import { BtSlimMediaSchema, WaMediaUnavailableSchema } from '../schemas/wa/media';
import { BtSlimLabelSchema } from '../schemas/wa/label';
import { BtSlimMessageAckSchema, BtSlimBatchMessageAckEntrySchema } from '../schemas/wa/message-ack';
import { TranscriptionResultSchema } from './speech-contracts.interface';
import { WireJidSchema, WireKeySchema } from '../contract/wire-identity';

// --- Args / Result schemas. Args schemas describe what the backend sends
// to the engine; Result schemas describe what the engine sends back. ---

export const WaCreateGroupArgsSchema = z.object({
  groupName: z.string(),
  participantChatIds: z.array(WireJidSchema),
});
export type WaCreateGroupArgs = z.infer<typeof WaCreateGroupArgsSchema>;

// Canonical payload-only emit (BE#93). Create now returns the FULL
// created group (same BtSlimGroup shape waGetGroupDataTool emits) so
// the v1 /v1/groups `create` is a thin proxy — no second
// waGetGroupDataTool round-trip, no synthesized fallback. Both engines
// (chrome ext + baileys pod) read the new group's metadata after
// creation and project it through the same get-group-data projector.
export const WaCreateGroupResultSchema = BtSlimGroupSchema;
export type WaCreateGroupResult = z.infer<typeof WaCreateGroupResultSchema>;

export const WaGetGroupDataArgsSchema = z.object({
  groupId: WireJidSchema,
});
export type WaGetGroupDataArgs = z.infer<typeof WaGetGroupDataArgsSchema>;

// Canonical payload-only emit (BE#93). The engine envelope
// `{success, data}` is stripped one layer up by callEngineTool's
// asEngineResponse (#51), so the schema parses the inner payload —
// BtSlimGroup directly. Both engines (chrome + baileys) emit
// BtSlimGroup at the handler boundary; the `{success, groupId, group,
// data: group}` chrome-only wrapper from buildGroupDataResponse is
// gone, killing the double-walk that safeCloneForPostMessage was
// turning into '[CIRCULAR_REFERENCE]' sentinels (BE#91 trigger).
export const WaGetGroupDataResultSchema = BtSlimGroupSchema;
export type WaGetGroupDataResult = z.infer<typeof WaGetGroupDataResultSchema>;

// ── Group mutations ─────────────────────────────────────────────────

/** Generic ack — every group mutation tool resolves with `{success: bool}`. */
const GroupMutationAckSchema = z.object({
  success: z.boolean().optional(),
});

export const WaUpdateGroupNameArgsSchema = z.object({
  groupId: WireJidSchema,
  groupName: z.string(),
});
export type WaUpdateGroupNameArgs = z.infer<typeof WaUpdateGroupNameArgsSchema>;
export const WaUpdateGroupNameResultSchema = GroupMutationAckSchema;
export type WaUpdateGroupNameResult = z.infer<typeof WaUpdateGroupNameResultSchema>;

/** Group settings — `announce` (admins-only post), `restrict` (admins-
 *  only edit info), `description` (group about text — routed to WAWeb's
 *  setGroupDescription, a separate path from the permission flags).
 *  Engine accepts a partial — only the keys provided are applied. */
export const WaUpdateGroupDescriptionArgsSchema = z.object({
  groupId: WireJidSchema,
  settings: z.object({
    announce: z.boolean().optional(),
    restrict: z.boolean().optional(),
    description: z.string().optional(),
  }),
});
export type WaUpdateGroupDescriptionArgs = z.infer<typeof WaUpdateGroupDescriptionArgsSchema>;
export const WaUpdateGroupDescriptionResultSchema = GroupMutationAckSchema;
export type WaUpdateGroupDescriptionResult = z.infer<typeof WaUpdateGroupDescriptionResultSchema>;

export const WaAddGroupParticipantArgsSchema = z.object({
  groupId: WireJidSchema,
  participantChatId: WireJidSchema,
});
export type WaAddGroupParticipantArgs = z.infer<typeof WaAddGroupParticipantArgsSchema>;
/** Participant mutations report a per-participant outcome. On a 403
 *  (invite-only) both engines send the invite v4 and report `invited`. */
export const GroupParticipantStatusSchema = z.enum(['added', 'removed', 'promoted', 'demoted', 'already', 'invited', 'not_found', 'refused']);
const GroupParticipantMutationResultSchema = GroupMutationAckSchema.extend({
  participants: z.array(z.object({ chatId: WireJidSchema, status: GroupParticipantStatusSchema })).optional(),
});

export const WaAddGroupParticipantResultSchema = GroupParticipantMutationResultSchema;
export type WaAddGroupParticipantResult = z.infer<typeof WaAddGroupParticipantResultSchema>;

export const WaRemoveGroupParticipantArgsSchema = z.object({
  groupId: WireJidSchema,
  participantChatId: WireJidSchema,
});
export type WaRemoveGroupParticipantArgs = z.infer<typeof WaRemoveGroupParticipantArgsSchema>;
export const WaRemoveGroupParticipantResultSchema = GroupParticipantMutationResultSchema;
export type WaRemoveGroupParticipantResult = z.infer<typeof WaRemoveGroupParticipantResultSchema>;

export const WaSetGroupAdminRightsArgsSchema = z.object({
  groupId: WireJidSchema,
  participantChatId: WireJidSchema,
});
export type WaSetGroupAdminRightsArgs = z.infer<typeof WaSetGroupAdminRightsArgsSchema>;
export const WaSetGroupAdminRightsResultSchema = GroupParticipantMutationResultSchema;
export type WaSetGroupAdminRightsResult = z.infer<typeof WaSetGroupAdminRightsResultSchema>;

export const WaRemoveGroupAdminRightsArgsSchema = z.object({
  groupId: WireJidSchema,
  participantChatId: WireJidSchema,
});
export type WaRemoveGroupAdminRightsArgs = z.infer<typeof WaRemoveGroupAdminRightsArgsSchema>;
export const WaRemoveGroupAdminRightsResultSchema = GroupParticipantMutationResultSchema;
export type WaRemoveGroupAdminRightsResult = z.infer<typeof WaRemoveGroupAdminRightsResultSchema>;

/** `fileDataUrl` is a `data:image/...;base64,...` URL (the engine reads
 *  the base64 payload and uploads). */
export const WaSetGroupPictureArgsSchema = z.object({
  groupId: WireJidSchema,
  fileDataUrl: z.string(),
  fileName: z.string().optional(),
  fileMimeType: z.string().optional(),
});
export type WaSetGroupPictureArgs = z.infer<typeof WaSetGroupPictureArgsSchema>;
export const WaSetGroupPictureResultSchema = GroupMutationAckSchema;
export type WaSetGroupPictureResult = z.infer<typeof WaSetGroupPictureResultSchema>;

export const WaLeaveGroupArgsSchema = z.object({
  groupId: WireJidSchema,
});
export type WaLeaveGroupArgs = z.infer<typeof WaLeaveGroupArgsSchema>;
export const WaLeaveGroupResultSchema = GroupMutationAckSchema;
export type WaLeaveGroupResult = z.infer<typeof WaLeaveGroupResultSchema>;

// ── Contacts ────────────────────────────────────────────────────────

import { BtSlimContactSchema } from '../schemas/wa/contact';
import { GUARDED_RELOAD_REASONS, GUARDED_RELOAD_OUTCOMES, GUARDED_RELOAD_DECISIONS } from '../notifier/guarded-reload';

export const WaGetProfilePicUrlArgsSchema = z.object({
  chatIds: z.array(WireJidSchema),
  /** @deprecated Legacy alias for `chatIds`. The backend dual-writes it during
   *  the rename transition so a not-yet-rebuilt engine still resolves the ids.
   *  @7.4 */
  contactIds: z.array(WireJidSchema).optional(),
});
export type WaGetProfilePicUrlArgs = z.infer<typeof WaGetProfilePicUrlArgsSchema>;
export const WaGetProfilePicUrlResultSchema = z.array(z.object({
  // Canonical resolved id. Optional only for the transition window — tighten to
  // required (and drop `contactId`) once the chrome extension emits `chatId`.
  chatId: WireJidSchema.optional(),
  /** @deprecated Legacy alias emitted by pre-rename engines. Consumers read
   *  `chatId ?? contactId`. @7.4 */
  contactId: WireJidSchema.optional(),
  profilePic: z.string().optional(),
}));
export type WaGetProfilePicUrlResult = z.infer<typeof WaGetProfilePicUrlResultSchema>;


// ── Common Groups ───────────────────────────────────────────────────
// Groups the authenticated user shares with the given contact (the
// "Groups in common" list WhatsApp shows on a contact's info panel).
// `contactChatId` is the contact's individual JID (e.g. 972...@c.us).
// Result mirrors the group list envelope — each shared group is
// projected to the same canonical BtSlimGroup shape so callers get
// names/participants without a second waGetGroupDataTool round-trip.
export const WaGetCommonGroupsArgsSchema = z.object({
  contactChatId: WireJidSchema,
});
export type WaGetCommonGroupsArgs = z.infer<typeof WaGetCommonGroupsArgsSchema>;
export const WaGetCommonGroupsResultSchema = z.object({
  groups: z.array(BtSlimGroupSchema).optional(),
  total: z.number().int().optional(),
});
export type WaGetCommonGroupsResult = z.infer<typeof WaGetCommonGroupsResultSchema>;

// ── Newsletters ────────────────────────────────────────────────────

import { BtSlimNewsletterSchema } from '../schemas/wa/newsletter';

export const WaCreateNewsletterArgsSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
});
export type WaCreateNewsletterArgs = z.infer<typeof WaCreateNewsletterArgsSchema>;
// Engine emits the canonical BtSlimNewsletter shape directly as the
// success envelope's `data` (gateway/baileys/handlers/newsletter.ts:42-54).
// `.passthrough()` keeps any future fields the engine adds from being
// stripped — boundary-parsing logs drift but does not 500 the caller.
export const WaCreateNewsletterResultSchema = BtSlimNewsletterSchema.passthrough();
export type WaCreateNewsletterResult = z.infer<typeof WaCreateNewsletterResultSchema>;

export const WaGetNewsletterArgsSchema = z.object({
  newsletterId: WireJidSchema,
});
export type WaGetNewsletterArgs = z.infer<typeof WaGetNewsletterArgsSchema>;
// Same canonical shape as Create (gateway/baileys/handlers/newsletter-read.ts:30-46).
// Engine can also emit `null` when nothing matched; modelled as
// `.nullable()` so the boundary parse accepts it and the service
// layer turns it into a 404.
export const WaGetNewsletterResultSchema = BtSlimNewsletterSchema.passthrough().nullable();
export type WaGetNewsletterResult = z.infer<typeof WaGetNewsletterResultSchema>;


// ── Engine / Utils ─────────────────────────────────────────────────

export const GetWaMeInfoArgsSchema = z.object({});
export type GetWaMeInfoArgs = z.infer<typeof GetWaMeInfoArgsSchema>;
/** Engine's "me" snapshot. `phone` is the digits-only phone string
 *  (engines resolve from their internal wid/JID and emit the bare form
 *  at the handler boundary — canonical single source per "no ambiguous
 *  shapes"). `name` (the WA pushname) is always emitted by both engines;
 *  `platform` is chrome-only (baileys leaves it undefined). The field
 *  names here are the PUBLIC snake_case names the v1 API returns as-is —
 *  engines emit this final shape, the v1 /v1/engines/me passes it through. */
export const GetWaMeInfoResultSchema = z.object({
  phone: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  platform: z.string().nullable().optional(),
});
export type GetWaMeInfoResult = z.infer<typeof GetWaMeInfoResultSchema>;

export const IsWhatsAppConnectedArgsSchema = z.object({});
export type IsWhatsAppConnectedArgs = z.infer<typeof IsWhatsAppConnectedArgsSchema>;
/** Engine connection snapshot. Both engines emit `connected` (required).
 *  Chrome additionally emits the WAWeb store fields `state`/`stream`/
 *  `has_synced`; baileys leaves them undefined (no equivalent concept).
 *  Canonical: required `connected` + optional chrome-only extras. The
 *  field names here are the PUBLIC snake_case names the v1 API returns
 *  as-is — engines emit this final shape, the v1 /v1/engines passes it
 *  through. */
export const IsWhatsAppConnectedResultSchema = z.object({
  connected: z.boolean(),
  state: z.string().nullable().optional(),
  stream: z.string().nullable().optional(),
  has_synced: z.boolean().nullable().optional(),
});
export type IsWhatsAppConnectedResult = z.infer<typeof IsWhatsAppConnectedResultSchema>;

export const WaReloadPageArgsSchema = z.object({
  /** Optional diagnostic reason forwarded to `killMyPod(reason)` so pod
   *  restarts carry the caller's cause (schedule retry, session automaton…). */
  reason: z.string().optional(),
});
export type WaReloadPageArgs = z.infer<typeof WaReloadPageArgsSchema>;
export const WaReloadPageResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaReloadPageResult = z.infer<typeof WaReloadPageResultSchema>;

export const WaGuardedReloadArgsSchema = z.object({
  reason: z.enum(GUARDED_RELOAD_REASONS),
}).strict();
export type WaGuardedReloadArgs = z.infer<typeof WaGuardedReloadArgsSchema>;
export const WaGuardedReloadResultSchema = z.object({
  outcome: z.enum(GUARDED_RELOAD_OUTCOMES),
  decision: z.enum(GUARDED_RELOAD_DECISIONS),
}).strict();
export type WaGuardedReloadResult = z.infer<typeof WaGuardedReloadResultSchema>;

export const WaTriggerLogoutArgsSchema = z.object({});
export type WaTriggerLogoutArgs = z.infer<typeof WaTriggerLogoutArgsSchema>;
export const WaTriggerLogoutResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaTriggerLogoutResult = z.infer<typeof WaTriggerLogoutResultSchema>;

export const WaValidatePhoneNumberOrChatIdArgsSchema = z.object({
  /** Phone (any common format) or JID. Both engines (chrome ext + baileys
   *  pod) accept `chatId` as the canonical key; `phoneOrChatId` is kept
   *  as a legacy alias on the engine side but not emitted by the backend. */
  chatId: WireJidSchema,
  /** Optional engine knob: chrome extension may force a usync probe to
   *  bypass the local validity cache (the wwebjs equivalent of `useForceUsync`).
   *  Baileys ignores it. Modeled here so the chrome page-context handler can
   *  read it without arg-shape drift. */
  options: z.object({
    useForceUsync: z.boolean().optional(),
  }).optional(),
});
export type WaValidatePhoneNumberOrChatIdArgs = z.infer<typeof WaValidatePhoneNumberOrChatIdArgsSchema>;
export const WaValidatePhoneNumberOrChatIdResultSchema = z.object({
  /** True when the engine confirmed the JID is a registered user.
   *  Always emitted (the whole purpose of the tool is yes/no). */
  valid: z.boolean(),
  /** Engine-resolved canonical WhatsApp JID, or null when the input
   *  isn't a registered WhatsApp account. */
  chatId: WireJidSchema.nullable().optional(),
  /** @deprecated Legacy alias for `chatId` emitted by pre-rename engines.
   *  Consumers read `chatId ?? formatted_chat_id`. @7.4 */
  formatted_chat_id: WireJidSchema.nullable().optional(),
  /** Why `valid` is false. Groups, lids and newsletters are checked against the
   *  engine's own store; an engine failure throws instead of `valid:false`. */
  errorCode: z.enum(['invalid_id', 'not_on_whatsapp', 'not_found']).optional(),
  /** Free-text reason; superseded by `errorCode`. @5.0b delete. */
  error: z.string().optional(),
});
export type WaValidatePhoneNumberOrChatIdResult = z.infer<typeof WaValidatePhoneNumberOrChatIdResultSchema>;

export const WaGetLinkPreviewArgsSchema = z.object({
  /** Engine accepts both `link` and `url`; backend emits `link` (the
   *  canonical key used by the chrome-extension page-context handler and
   *  the baileys pod's link-preview handler). `url` is the legacy alias —
   *  modeled here so the chrome page-context handler that historically
   *  reads `data.url` still type-checks (live behavior is unchanged —
   *  backend emits `link`, handler reads `url` and resolves to
   *  `undefined`, which is a separate pre-existing bug). */
  link: z.string().optional(),
  url: z.string().optional(),
});
export type WaGetLinkPreviewArgs = z.infer<typeof WaGetLinkPreviewArgsSchema>;
/** Engine wraps the preview at the top level (no `{preview: ...}`
 *  envelope) — title/description/thumbnail/canonicalUrl/matchedText sit
 *  directly on the response. */
export const WaGetLinkPreviewResultSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  canonicalUrl: z.string().optional(),
  matchedText: z.string().optional(),
  /** Raw base64 bytes (no `data:` prefix) or empty string when WA has
   *  no thumbnail. */
  thumbnail: z.string().optional(),
});
export type WaGetLinkPreviewResult = z.infer<typeof WaGetLinkPreviewResultSchema>;


export const WaQueryMessagesArgsSchema = z.object({
  chatId: WireJidSchema.optional(),
  searchQuery: z.string().optional(),
  /** OR keyword-set (§4.3): a message matches if body/caption/filename contains
   *  ANY of these terms (case-insensitive). Combined with `searchQuery` when
   *  both are present. Runs in ONE local-scan pass — a credential/invoice hunt
   *  (FTP|PASS|USER|HOST) is one call instead of N. */
  searchQueryAny: z.array(z.string()).optional(),
  /** Media/document filters (invoice/document hunts). Applied in the same
   *  predicate chain as `messageTypes`. */
  mime: z.string().optional(),             // prefix match, e.g. "application/pdf" | "image/"
  filenameContains: z.string().optional(), // substring match on document filename
  hasMedia: z.boolean().optional(),        // true = only media msgs; false = only non-media
  sender: z.string().optional(),           // substring match on sender JID
  /** Page size; resolved with `resolveLimit` (WA_READ_LIMITS). */
  limit: z.number().optional(),
  /** Counts from the window's origin: the newest message for `latest`, the
   *  oldest for `history`. */
  offset: z.number().optional(),
  /** `latest` (when absent) = the window starts at the newest message;
   *  `history` = it starts at the oldest. Rows are ALWAYS ascending by `t`,
   *  in both modes. Search follows the same rule. */
  mode: z.enum(['history', 'latest']).optional(),
  cutoffDate: z.string().optional(),
  latestDate: z.string().optional(),
  /** When the local store is exhausted, the engine may fetch older history
   *  from the phone; it reports that with `backfilling` / `paging.canLoadFromPhone`.
   *  No other read triggers a network fetch. */
  loadFromPhoneIfNeeded: z.boolean().optional(),
  /** Engine-side filter to specific WAWeb message subtypes (`image`,
   *  `document`, `audio`, ...). REST exposes `message_types`; service
   *  layer translates to this camel-case wire name. Same SSoT as
   *  `MessageTypeEnum` in `api/v1/lib/message-types.ts`. Accepts BOTH the
   *  legacy v1 names and the WA Web names (`waType`, e.g. `buttons_response`):
   *  validated against the bt-common type table (isMessageTypeFilterName), so a
   *  name the table does not know is rejected at the engine boundary. The
   *  inferred type is `string[]` (was a 13-name union): a consumer typed on the
   *  old union (chrome's `searchMessages`) widens its own type when it re-pins. */
  messageTypes: z.array(z.string().refine(isMessageTypeFilterName, { message: 'unknown message type' })).optional(),
  /** Per-tier source gates (default TRUE when absent — sent only to DISABLE a
   *  tier). `isSourceWa` includes the live WhatsApp store; `isSourceVault`
   *  includes the client-side vault (merged when the store came up short). With
   *  both true it behaves as before (WA→vault); e.g. `isSourceWa:false` returns
   *  vault-only. The DB backup tier is gated separately (`isSourceBackup` on the
   *  wa-messages-backup read). A debug aid to verify each tier independently. */
  isSourceWa: z.boolean().optional(),
  isSourceVault: z.boolean().optional(),
});
export type WaQueryMessagesArgs = z.infer<typeof WaQueryMessagesArgsSchema>;

export const WaQueryMessagesResultSchema = z.object({
  // BE#509: an empty since/until window makes the engine emit the envelope with
  // NO `messages` key. `.default([])` normalizes that omission to an empty page
  // at the boundary parse (callEngineTool) so a no-match window returns a 200
  // with `{ messages: [] }` instead of a 502 (`messages: Required`).
  messages: z.array(BtSlimMessageSchema).default([]),
  /** Rows matching every filter, before paging, in the engine's local store.
   *  Always emitted by a contract engine. @5.0b required. */
  totalMessages: z.number().int().nullable().optional(),
  paging: z.object({
    /** `offset + returned < totalMessages`. Always emitted by a contract
     *  engine. @5.0b required. */
    hasMoreInStore: z.boolean().optional(),
    /** Older history may exist on the phone. */
    canLoadFromPhone: z.boolean().optional(),
  }).optional(),
  /** The engine started fetching older history because the store is exhausted. */
  backfilling: z.boolean().optional(),
  /** Coverage envelope (§4.5) — makes "no results" unambiguous. `[]` +
   *  (truncated|indexOnly) = INCONCLUSIVE (harness must broaden/page before
   *  denying); `[]` + full coverage = honest "searched N messages, none
   *  matched". Populated on the search path (searchQuery / searchQueryAny). */
  coverage: z.object({
    /** Rows the predicate was evaluated on (0 = WA search index only). */
    messagesScanned: z.number().int(),
    /** The scan stopped at a cap before exhausting the store. */
    truncated: z.boolean(),
    /** No local scan ran; results came only from WA's index (misses cold
     *  chats / captions / filenames / newsletters). */
    indexOnly: z.boolean(),
    /** ISO timestamp of the oldest row evaluated, when known. */
    oldestScannedIso: z.string().optional(),
  }).optional(),
});
export type WaQueryMessagesResult = z.infer<typeof WaQueryMessagesResultSchema>;

// ── Chats domain ────────────────────────────────────────────────────────

export const WaGetChatObjectByChatIdArgsSchema = z.object({
  chatId: WireJidSchema,
});
export type WaGetChatObjectByChatIdArgs = z.infer<typeof WaGetChatObjectByChatIdArgsSchema>;
// Canonical payload-only emit (BE#93). Engine emits BtSlimChat
// directly; the legacy `{chat}` wrapper has been dropped. Null on
// not-found so the service layer can map to 404.
// Not-found rule: a read with a nullable result returns `null`; a non-nullable
// read (group data, participants of an unknown group) throws `not_found`; a
// filter that cannot be evaluated never fails the query (the row just does not
// match). Before the initial sync, every read throws `not_connected`.
export const WaGetChatObjectByChatIdResultSchema = BtSlimChatSchema.nullable();
export type WaGetChatObjectByChatIdResult = z.infer<typeof WaGetChatObjectByChatIdResultSchema>;

export const WaQueryContactsAndChatsArgsSchema = z.object({
  includeContacts: z.boolean().optional(),
  /** `false` omits the `chats` key. */
  includeChats: z.boolean().optional(),
  searchToken: z.string().optional(),
  /** Resolved with `resolveLimit` (WA_READ_LIMITS: all rows when absent). */
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  /** Kind filters; they apply on both the list and the search path. */
  filterGroups: z.boolean().optional(),
  filterContacts: z.boolean().optional(),
  filterNewsletters: z.boolean().optional(),
  /** When false/absent, rows whose `name` is null are excluded. */
  includeWithoutName: z.boolean().optional(),
  /** Emits each chat row's `lastMessage`. */
  includeLastMessage: z.boolean().optional(),
  /** When false/absent the engine DROPS archived chats from `chats`. */
  includeArchive: z.boolean().optional(),
  /** BE#416 — user-triggered contact refresh. When true, the Baileys engine
   *  pulls the contact app-state collection on demand (cooldown-bounded) before
   *  reading its ContactStore, so a contact saved on the phone since the last
   *  sync surfaces without a full re-pair. Ignored by the Chrome engine (it reads
   *  WhatsApp Web's live Store.Contact). Set by the backend when the web app sends
   *  `$forceContactsUpdate`. */
  forceResync: z.boolean().optional(),
});
export type WaQueryContactsAndChatsArgs = z.infer<typeof WaQueryContactsAndChatsArgsSchema>;
export const WaQueryContactsAndChatsResultSchema = z.object({
  chats: z.array(BtSlimChatSchema).optional(),
  /** PERSONS only (no groups, no newsletters); an unresolved `@lid` stays raw.
   *  The same rule on the list and the search path — the search path returns
   *  the matching persons only, not a union with chats. */
  contacts: z.array(BtSlimContactSchema).optional(),
  /** Rows after dedupe and filters, before paging. */
  totalChats: z.number().optional(),
  totalContacts: z.number().optional(),
  // Effective pagination echoed by the engine so /v1 list endpoints report
  // exactly what the engine used (`offset` → `skip`). Optional for back-compat
  // with engine builds that predate these fields.
  limit: z.number().optional(),
  skip: z.number().optional(),
});
export type WaQueryContactsAndChatsResult = z.infer<typeof WaQueryContactsAndChatsResultSchema>;

// ── getLatestChatsAndMessages ────────────────────────────────────────────
export const WaGetLatestChatsAndMessagesArgsSchema = z.object({
  /** Total chats to return (pinned included); resolved with `resolveLimit`. */
  numberOfChats: z.number().int().positive().optional(),
  /** Latest messages to return per chat; resolved with `resolveLimit`. */
  numberOfMessages: z.number().int().nonnegative().optional(),
});
export type WaGetLatestChatsAndMessagesArgs = z.infer<typeof WaGetLatestChatsAndMessagesArgsSchema>;

/** A slim chat with its latest messages attached. */
export const WaLatestChatWithMessagesSchema = z.intersection(
  BtSlimChatSchema,
  z.object({ messages: z.array(BtSlimMessageSchema) }),
);
export type WaLatestChatWithMessages = z.infer<typeof WaLatestChatWithMessagesSchema>;

export const WaGetLatestChatsAndMessagesResultSchema = z.object({
  // BE#509 (same class): no chats → engine omits `chats` → default to [].
  chats: z.array(WaLatestChatWithMessagesSchema).default([]),
  /** Non-archived, titled chat count before the numberOfChats slice. */
  totalChats: z.number(),
  /** Echo of the effective caps. */
  numberOfChats: z.number(),
  numberOfMessages: z.number(),
});
export type WaGetLatestChatsAndMessagesResult = z.infer<typeof WaGetLatestChatsAndMessagesResultSchema>;

// ── queryVaultMessages (all-chats, cursor-paged) ─────────────────────────
/** Strict-before compound cursor over the client-side vault: send-time in MS
 *  plus the unique msgId tiebreak (equal-ms rows never dupe/skip). */
export const WaVaultCursorSchema = z.object({
  t: z.number(),
  msgId: z.string(),
});
export type WaVaultCursor = z.infer<typeof WaVaultCursorSchema>;

export const WaQueryVaultMessagesArgsSchema = z.object({
  /** Max rows to return, newest first; resolved with `resolveLimit`. */
  limit: z.number().int().positive().optional(),
  /** Return only rows strictly older than this cursor (omit for newest page). */
  before: WaVaultCursorSchema.optional(),
});
export type WaQueryVaultMessagesArgs = z.infer<typeof WaQueryVaultMessagesArgsSchema>;

/** The vault's slim projection (mirrors `VaultSlimMessage`). A dedicated shape
 *  rather than `BtSlimMessage` because the all-chats consumer needs `chatId`
 *  (and the `source` tag) preserved over the wire. */
export const WaVaultSlimMessageSchema = z.object({
  waMessageKey: z.object({ _serialized: WireKeySchema }),
  msgId: z.string(),
  chatId: WireJidSchema,
  body: z.string(),
  caption: z.string().nullable(),
  filename: z.string().nullable(),
  from: WireJidSchema,
  author: WireJidSchema.nullable(),
  fromMe: z.boolean(),
  /** Unix SECONDS. */
  t: z.number(),
  type: z.string(),
  notifyName: z.string().nullable(),
  hasAttachedMedia: z.boolean(),
  source: z.literal('vault'),
});
export type WaVaultSlimMessage = z.infer<typeof WaVaultSlimMessageSchema>;

export const WaQueryVaultMessagesResultSchema = z.object({
  // BE#509 (same class): empty vault page omits `messages` → default to [].
  messages: z.array(WaVaultSlimMessageSchema).default([]),
  /** Cursor to pass as `before` for the next (older) page; null when drained. */
  nextCursor: WaVaultCursorSchema.nullable(),
  hasMore: z.boolean(),
});
export type WaQueryVaultMessagesResult = z.infer<typeof WaQueryVaultMessagesResultSchema>;

export const WaGetChatParticipantsArgsSchema = z.object({
  chatId: WireJidSchema,
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  /** Case-insensitive substring filter on the participant's resolved
   *  name OR phone/JID. When present the engine filters the full
   *  participant set BEFORE paginating, and `total` reflects the count
   *  of the name-matched set (not the page length). Absent → list all
   *  participants (total = full member count). */
  searchToken: z.string().optional(),
});
export type WaGetChatParticipantsArgs = z.infer<typeof WaGetChatParticipantsArgsSchema>;
// Paged envelope: the
// engine owns offset/limit pagination + searchToken filtering and reports
// the correct `total` (count of the name-matched set, or the full member
// count when no searchToken). The older bare-array emit could not carry a
// total, so the v1 service had to guess `skip + page.length` — wrong for
// any group larger than one page (a 930-member group reported total=5).
// `limit`/`skip` are echoed back so the service reports exactly what the
// engine used. All three fields optional for back-compat with engine
// builds that predate this shape.
export const WaGetChatParticipantsResultSchema = z.object({
  participants: z.array(BtSlimParticipantSchema).optional(),
  total: z.number().int().optional(),
  limit: z.number().int().optional(),
  skip: z.number().int().optional(),
});
export type WaGetChatParticipantsResult = z.infer<typeof WaGetChatParticipantsResultSchema>;

export const WaSendSeenArgsSchema = z.object({ chatId: WireJidSchema });
export type WaSendSeenArgs = z.infer<typeof WaSendSeenArgsSchema>;
// Mirrors the `{result}` envelope every chat-mutation handler emits (see
// ChatMutationResultEnvelopeSchema below): `result` is the underlying WAWeb
// sendSeen boolean so callers can tell whether the chat was actually marked.
export const WaSendSeenResultSchema = z.object({ result: z.boolean(), error: z.string().optional() }).strict();
export type WaSendSeenResult = z.infer<typeof WaSendSeenResultSchema>;

export const WaOpenChatArgsSchema = z.object({ chatId: WireJidSchema });
export type WaOpenChatArgs = z.infer<typeof WaOpenChatArgsSchema>;
// Canonical payload-only emit (BE#93). The engine envelope's `success`
// is stripped one layer up; only the echoed chatId remains on the
// payload, surfaced so callers can confirm which chat was opened.
export const WaOpenChatResultSchema = z.object({
  chatId: WireJidSchema.optional(),
});
export type WaOpenChatResult = z.infer<typeof WaOpenChatResultSchema>;

export const WaLoadMessagesFromPhoneArgsSchema = z.object({
  chatId: WireJidSchema,
  limit: z.number().int().optional(),
});
export type WaLoadMessagesFromPhoneArgs = z.infer<typeof WaLoadMessagesFromPhoneArgsSchema>;
export const WaLoadMessagesFromPhoneResultSchema = z.object({
  success: z.boolean().optional(),
  totalMessages: z.number().int().nullable().optional(),
  totalMessagesAddedByLoadFromPhoneIfNeeded: z.number().int().nullable().optional(),
  canLoadMore: z.boolean().optional(),
  error: z.string().nullable().optional(),
});
export type WaLoadMessagesFromPhoneResult = z.infer<typeof WaLoadMessagesFromPhoneResultSchema>;

export const WaGetMessageByKeyArgsSchema = z.object({
  /** Canonical wire-format key:
   *  `"<fromMe>_<chatJid>_<id>[_<participant>]"`. The single source of
   *  truth — callers that only have a bare id + chatId should rebuild
   *  the wire string via `bt-common/utils/message-key.ts` BEFORE
   *  dispatch. Per "no ambiguous shapes" — earlier shape carried both
   *  `messageKey` (object union) and `waMessageKey` (string); the
   *  object form is gone, only the string remains. */
  waMessageKey: WireKeySchema,
  chatId: WireJidSchema.optional(),
});
export type WaGetMessageByKeyArgs = z.infer<typeof WaGetMessageByKeyArgsSchema>;
// Canonical payload-only emit (BE#93). Engine emits BtSlimMessage
// directly; the legacy `{info}` wrapper has been dropped. Null on
// not-found so the service layer can map to 404.
export const WaGetMessageByKeyResultSchema = BtSlimMessageSchema.nullable();
export type WaGetMessageByKeyResult = z.infer<typeof WaGetMessageByKeyResultSchema>;

export const WaGetMessageAckArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  chatId: WireJidSchema.optional(),
});
export type WaGetMessageAckArgs = z.infer<typeof WaGetMessageAckArgsSchema>;
export const WaGetMessageAckResultSchema = BtSlimMessageAckSchema;
export type WaGetMessageAckResult = z.infer<typeof WaGetMessageAckResultSchema>;

// ── Poll results (who voted for which option) ───────────────────────
// Read-only snapshot of a poll's CURRENT votes: latest vote per voter, read
// from the engine's own vote store (WA Web's poll-vote table / the Baileys
// pod's merged poll_updates). Option names come from the poll creation
// message; when the engine can't map a vote to an option (missing or
// unmatched creation options) the vote is still returned, with
// `decoded:false` — never dropped, so `totalVoters` stays truthful.
// A retracted vote (empty selection) is not a vote and is omitted.
export const WaGetPollResultsArgsSchema = z.object({
  /** Wire key of the POLL CREATION message. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  chatId: WireJidSchema.optional(),
});
export type WaGetPollResultsArgs = z.infer<typeof WaGetPollResultsArgsSchema>;

export const WaPollResultVoteSchema = z.object({
  /** Voter jid as the engine holds it (often `@lid`). */
  voter: WireJidSchema,
  voterName: z.string().optional(),
  /** Option indexes (0-based, creation order). Empty when the engine only
   *  holds the option HASHES and couldn't match them (Baileys, decoded:false). */
  selectedOptionLocalIds: z.array(z.number().int()),
  /** Names for the selected options; empty when `decoded` is false. */
  selectedOptionNames: z.array(z.string()),
  /** False = this voter voted but the choice couldn't be mapped to an option. */
  decoded: z.boolean(),
  /** Vote time, epoch ms (sender timestamp). */
  votedAt: z.number().optional(),
});
export type WaPollResultVote = z.infer<typeof WaPollResultVoteSchema>;

export const WaPollResultOptionSchema = z.object({
  localId: z.number().int(),
  name: z.string(),
  voteCount: z.number().int(),
  voters: z.array(WireJidSchema),
});
export type WaPollResultOption = z.infer<typeof WaPollResultOptionSchema>;

// Null on not-found (message not in the engine's store) so the service layer
// maps it to 404. A message that exists but is not a poll → `invalid_input`.
export const WaGetPollResultsResultSchema = z.object({
  waMessageKey: WireKeySchema,
  chatId: WireJidSchema,
  question: z.string().optional(),
  /** Max selectable options; 1 = single choice, 0 = unlimited (WA semantics). */
  selectableOptionsCount: z.number().int().optional(),
  options: z.array(WaPollResultOptionSchema),
  votes: z.array(WaPollResultVoteSchema),
  totalVoters: z.number().int(),
  undecodedVotes: z.number().int(),
}).nullable();
export type WaGetPollResultsResult = z.infer<typeof WaGetPollResultsResultSchema>;

export const WaGetMessagesAckDataArgsSchema = z.object({
  messageKeys: z.array(
    z.object({
      waMessageKey: WireKeySchema.optional(),
      messageId: z.string().optional(),
    }),
  ),
  chatId: WireJidSchema.optional(),
});
export type WaGetMessagesAckDataArgs = z.infer<typeof WaGetMessagesAckDataArgsSchema>;
// Canonical payload-only emit (BE#93). Engine emits the array
// directly; the legacy `{data}` wrapper has been dropped. The outer
// `{success, data}` engine envelope is already stripped by
// callEngineTool's asEngineResponse.
export const WaGetMessagesAckDataResultSchema = z.array(BtSlimBatchMessageAckEntrySchema);
export type WaGetMessagesAckDataResult = z.infer<typeof WaGetMessagesAckDataResultSchema>;

export const WaGetMessageMediaArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  chatId: WireJidSchema.optional(),
  /** Required (no engine default):
   *  - `preview`: thumbnail + metadata (+ extractedText/transcription where the engine can);
   *  - `full`: a signed `mediaUrl` from the backend upload (`POST /actions cmd=upload-mcp-media`)
   *    — URL only, never inline;
   *  - `agent-vision` (BE#366): always inline, a DOWNSIZED JPEG (≤1024px long edge) for
   *    agent vision; falls back to `full` when the resize fails. */
  mode: z.enum(['preview', 'full', 'agent-vision']),
  /** UI-side opt-in for audio transcription via Whisper. MCP/SDK
   *  callers leave this off (Whisper is too slow for tool RTT). */
  transcribeAudio: z.boolean().optional(),
});
export type WaGetMessageMediaArgs = z.infer<typeof WaGetMessageMediaArgsSchema>;
/** The media tool emits ONE of three shapes depending on `mode` and the
 *  page-context lookup outcome:
 *    1. Full (mode='full') — canonical BtSlimMedia (signed GCS URL +
 *       metadata, produced after the content-script's backend-proxied
 *       upload).
 *    2. Preview (mode='preview') — inline thumbnail + extracted text +
 *       metadata, no upload round-trip.
 *    3. Sentinel — `{messageNotFound: true}` when the resolved message id
 *       didn't match the requested key (#49 root cause).
 *  Modeled as a union so the notifier handler can return any of the
 *  three without lying via `as`. Backend boundary-parse distinguishes
 *  branches by presence of `url` (full) vs `thumbnailDataUrl` /
 *  `extractedText` / `metadata` (preview) vs `messageNotFound` (sentinel). */
/** Preview metadata — what a caller needs to route without fetching the file.
 *  Every field nullish (absent / null = unknown). */
export const WaMediaPreviewMetadataSchema = z.object({
  mimetype: z.string().nullish(),
  /** Byte size. */
  fileSize: z.number().nullish(),
  fileName: z.string().nullish(),
  width: z.number().nullish(),
  height: z.number().nullish(),
  /** Seconds, as WAWeb stores it: a string (e.g. "56"). */
  duration: z.string().nullish(),
  pageCount: z.number().nullish(),
}).passthrough();
export type WaMediaPreviewMetadata = z.infer<typeof WaMediaPreviewMetadataSchema>;

export const WaGetMessageMediaPreviewSchema = z.object({
  kind: z.literal('preview'),
  thumbnailDataUrl: z.string().nullable().optional(),
  extractedText: z.string().nullable().optional(),
  language: z.string().nullable().optional(),
  /** Structured transcription result — present only when the caller
   *  passed `transcribeAudio: true` and the message is audio. Additive:
   *  `extractedText`/`language` keep carrying the same text/language for
   *  existing consumers; this field adds flags/model/timings so callers
   *  that ACT on transcripts (agent ingress) can gate on quality. */
  transcription: TranscriptionResultSchema.nullable().optional(),
  metadata: WaMediaPreviewMetadataSchema.optional(),
  /** Why the media is unavailable; null / absent = available (the same
   *  spelling as BtSlimMedia). */
  mediaUnavailable: WaMediaUnavailableSchema.nullable().optional(),
  /** Page-side internal — present only on the page→content-script hop
   *  before the content-script bridge extracts text from PDF/text bytes.
   *  Stripped before content-script→backend MQTT publish. */
  _bytesForExtraction: z.object({
    dataBase64: z.string(),
    mimetype: z.string(),
  }).nullable().optional(),
});
export const WaGetMessageMediaSentinelSchema = z.object({
  kind: z.literal('not_found'),
  messageNotFound: z.literal(true),
}).passthrough();
// Full-media variant adds the `kind` discriminator on top of BtSlimMedia
// (BtSlim itself stays kind-less for use in other contexts that don't need
// discrimination — e.g. raw media field on messages).
export const WaGetMessageMediaFullSchema = BtSlimMediaSchema.extend({
  kind: z.literal('full'),
});
export const WaGetMessageMediaResultSchema = z.discriminatedUnion('kind', [
  WaGetMessageMediaFullSchema,
  WaGetMessageMediaPreviewSchema,
  WaGetMessageMediaSentinelSchema,
]);
export type WaGetMessageMediaResult = z.infer<typeof WaGetMessageMediaResultSchema>;
export type WaGetMessageMediaPreview = z.infer<typeof WaGetMessageMediaPreviewSchema>;
export type WaGetMessageMediaSentinel = z.infer<typeof WaGetMessageMediaSentinelSchema>;

/**
 * Page-side intermediate shape for waGetMessageMediaTool. The page handler
 * runs inside the WAWeb page context (no chrome.runtime); it can produce
 * raw `File` objects for the full path but cannot upload them. The
 * content-script dispatcher (`wa-tools-notifier-handlers.ts`) consumes this
 * intermediate, uploads the File bytes via the backend-proxied multipart
 * endpoint, then projects into the final `WaGetMessageMediaResult` wire
 * shape that flows to the backend over MQTT. File objects survive the
 * page→content-script `postMessage` hop via structured clone.
 */
export const WaGetMessageMediaPageFullSchema = z.object({
  kind: z.literal('full'),
  mediaFile: z.instanceof(File).optional(),
  thumbnailFile: z.instanceof(File).optional(),
  originalQuality: z.boolean().optional(),
  /** Why the bytes could not be downloaded (no mediaFile): the page classifies
   *  the failure (expired / fetching / awaiting_sender / error) and the
   *  content side reports it on the full result. */
  mediaUnavailable: WaMediaUnavailableSchema.optional(),
});
export const WaGetMessageMediaPageResultSchema = z.discriminatedUnion('kind', [
  WaGetMessageMediaPageFullSchema,
  WaGetMessageMediaPreviewSchema,
  WaGetMessageMediaSentinelSchema,
]);
export type WaGetMessageMediaPageResult = z.infer<typeof WaGetMessageMediaPageResultSchema>;


export const WaReactToMessageArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  /** Reaction emoji. Empty string clears the reaction. */
  text: z.string(),
  chatId: WireJidSchema.optional(),
});
export type WaReactToMessageArgs = z.infer<typeof WaReactToMessageArgsSchema>;
export const WaReactToMessageResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaReactToMessageResult = z.infer<typeof WaReactToMessageResultSchema>;

// ── Forward a message ───────────────────────────────────────────────
export const WaForwardMessageArgsSchema = z.object({
  /** Canonical wire-format key of the message to forward. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  /** Destination chat ids (@c.us / @g.us / @newsletter). Forwarded to each. */
  toChatIds: z.array(WireJidSchema),
  /** Source chat id — optional hint; the message is resolved by key regardless. */
  chatId: WireJidSchema.optional(),
});
export type WaForwardMessageArgs = z.infer<typeof WaForwardMessageArgsSchema>;
export const WaForwardMessageResultSchema = z.object({
  success: z.boolean().optional(),
  /** The chat ids the message was successfully forwarded to. */
  forwardedTo: z.array(WireJidSchema).optional(),
});
export type WaForwardMessageResult = z.infer<typeof WaForwardMessageResultSchema>;

// ── Star / unstar a message ─────────────────────────────────────────
export const WaStarMessageArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  /** true = star, false = unstar. */
  star: z.boolean(),
  chatId: WireJidSchema.optional(),
});
export type WaStarMessageArgs = z.infer<typeof WaStarMessageArgsSchema>;
export const WaStarMessageResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaStarMessageResult = z.infer<typeof WaStarMessageResultSchema>;

// ── Delete a message ────────────────────────────────────────────────
export const WaDeleteMessageArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  /** true = "delete for everyone" (revoke — permission-gated by WA);
   *  false/omitted = "delete for me" (local delete). */
  forEveryone: z.boolean().optional(),
  chatId: WireJidSchema.optional(),
});
export type WaDeleteMessageArgs = z.infer<typeof WaDeleteMessageArgsSchema>;
export const WaDeleteMessageResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaDeleteMessageResult = z.infer<typeof WaDeleteMessageResultSchema>;

// ── Pin / unpin a message ───────────────────────────────────────────
/** Default pin expiry (7 days, in seconds) when the caller omits `duration`.
 *  Mirrors WhatsApp's default and whatsapp-web.js' pin behaviour. */
export const DEFAULT_PIN_DURATION_SECONDS = 604800;

export const WaPinMessageArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  /** Pin expiry in seconds. Optional — defaults to 7 days. */
  duration: z.number().int().positive().optional(),
  chatId: WireJidSchema.optional(),
});
export type WaPinMessageArgs = z.infer<typeof WaPinMessageArgsSchema>;
export const WaPinMessageResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaPinMessageResult = z.infer<typeof WaPinMessageResultSchema>;

export const WaUnpinMessageArgsSchema = z.object({
  /** Canonical wire-format key. See WaGetMessageByKeyArgsSchema. */
  waMessageKey: WireKeySchema,
  chatId: WireJidSchema.optional(),
});
export type WaUnpinMessageArgs = z.infer<typeof WaUnpinMessageArgsSchema>;
export const WaUnpinMessageResultSchema = z.object({
  success: z.boolean().optional(),
});
export type WaUnpinMessageResult = z.infer<typeof WaUnpinMessageResultSchema>;

// ── Send (sendMessageThunkTool) ─────────────────────────────────────
//
// Backend's POST /v1/messages direct-send path dispatches through this.
// Engine's MqttCommandRouter receives the userMessage envelope; the
// per-action handler (paramsFromUserMessage in extension's
// message-app-context.ts) reads contactList[0].id, message, asset,
// poll, linkPreview, quotedMessageId.
//
// The result schema captures every field the post-ack payload can
// carry — `id` + `chatId` + `fromMe` + `timestamp` on success;
// `deduplicated: true` + `lockId` + `reason` when the Phase B engine
// router short-circuits a duplicate cmd-id delivery (recipient already
// got the message; surface as success).
import { UserMessageSchema } from '../schemas/bt/user-message';
export const WaSendMessageThunkArgsSchema = z.object({
  userMessage: UserMessageSchema,
  /** Per-send options. */
  options: z
    .object({
      /** Show the "typing…" indicator before a text send. Default true. */
      withTyping: z.boolean().optional(),
      /** Extra typing duration in seconds, ADDED on top of the built-in
       *  0.4–0.8s random jitter. Default 0. */
      typingSeconds: z.number().optional(),
      /** FE#360 — this send was asked for by a PERSON (send-now / retry button)
       *  rather than by the automatic re-dispatch. A scheduled Status is
       *  at-most-once per engine; this flag is how a caller says "the user asked
       *  for it again", so the ledger lets the repost through instead of
       *  silently refusing a button press. Absent → treated as automatic. */
      isSendMessageNow: z.boolean().optional(),
    })
    .optional(),
});
export type WaSendMessageThunkArgs = z.infer<typeof WaSendMessageThunkArgsSchema>;
export const WaSendMessageThunkResultSchema = z.object({
  /** The sent message's wire key string. A contract engine always emits it on
   *  success; one that cannot obtain the key id throws `ambiguous` instead.
   *  @5.0b required. */
  waMessageKey: WireKeySchema.optional(),
  chatId: WireJidSchema.optional(),
  /** The wire messageSecret token of this send (see `resolveWireSecret`). */
  secret: z.string().optional(),
  /** The sent message, for optimistic UI. */
  message: BtSlimMessageSchema.optional(),
  /** Phase B engine-cmd dedup hit. */
  deduplicated: z.boolean().optional(),
  lockId: z.string().optional(),
  reason: z.string().optional(),
  // --- Rollout window: what older engines still send and the Release-A backend
  //     send readers still read (scheduled-messages.service.ts:447-466,
  //     agent-send.ts:198-214). The /v1 boundary returns the PARSED result, so
  //     deleting them now would strip the key of every older engine's send.
  //     @5.0b delete (the schema fields; 5.9c deletes the readers, same pin). ---
  response: z.unknown().optional(),
  waMeInfo: z.unknown().optional(),
  id: z.string().optional(),
  messageId: z.string().optional(),
  userMessageId: z.string().optional(),
  fromMe: z.boolean().optional(),
  timestamp: z.number().optional(),
});
export type WaSendMessageThunkResult = z.infer<typeof WaSendMessageThunkResultSchema>;

// ── Chat mutations ──────────────────────────────────────────────────
//
// Shared `{chatId}` arg shape for the chat-mutation family. Backend
// emits `{chatId}` only; the page handlers all read `data.chatId`
// (`PageMessageService.wireHandler` lines 736-801).

const ChatIdOnlyArgsSchema = z.object({ chatId: WireJidSchema });

/** Result shape every page-side chat-mutation wireHandler emits via the
 *  `{result}` envelope — `clearMessages`, `deleteChat`, `archive/unarchive`,
 *  `pin/unpin`, `markChatUnread`, `sendStateTyping/Recording`,
 *  `clearState`, `syncHistory`. The inner `result` is whatever the
 *  underlying WAWeb / wwebjs call returned — opaque to the contract. */
const ChatMutationResultEnvelopeSchema = z.object({
  /** `true` when the mutation succeeded, `false` when WAWeb returned a
   *  negative result (e.g. mute called on an already-muted chat). Throws
   *  in the page handler surface as the dispatcher's error envelope
   *  (`{success:false, error}`), NOT here. */
  result: z.boolean(),
  /** Set when `result === false` — carries the underlying WAWeb diagnostic
   *  so the caller can distinguish "no-op refused by WA" from generic
   *  failure. Absent on `result: true`. */
  error: z.string().optional(),
}).strict();

export const WaClearMessagesArgsSchema = ChatIdOnlyArgsSchema;
export type WaClearMessagesArgs = z.infer<typeof WaClearMessagesArgsSchema>;
export const WaClearMessagesResultSchema = ChatMutationResultEnvelopeSchema;
export type WaClearMessagesResult = z.infer<typeof WaClearMessagesResultSchema>;

export const WaDeleteChatArgsSchema = ChatIdOnlyArgsSchema;
export type WaDeleteChatArgs = z.infer<typeof WaDeleteChatArgsSchema>;
export const WaDeleteChatResultSchema = ChatMutationResultEnvelopeSchema;
export type WaDeleteChatResult = z.infer<typeof WaDeleteChatResultSchema>;

export const WaArchiveChatArgsSchema = ChatIdOnlyArgsSchema;
export type WaArchiveChatArgs = z.infer<typeof WaArchiveChatArgsSchema>;
export const WaArchiveChatResultSchema = ChatMutationResultEnvelopeSchema;
export type WaArchiveChatResult = z.infer<typeof WaArchiveChatResultSchema>;

export const WaUnarchiveChatArgsSchema = ChatIdOnlyArgsSchema;
export type WaUnarchiveChatArgs = z.infer<typeof WaUnarchiveChatArgsSchema>;
export const WaUnarchiveChatResultSchema = ChatMutationResultEnvelopeSchema;
export type WaUnarchiveChatResult = z.infer<typeof WaUnarchiveChatResultSchema>;

export const WaPinChatArgsSchema = ChatIdOnlyArgsSchema;
export type WaPinChatArgs = z.infer<typeof WaPinChatArgsSchema>;
export const WaPinChatResultSchema = ChatMutationResultEnvelopeSchema;
export type WaPinChatResult = z.infer<typeof WaPinChatResultSchema>;

export const WaUnpinChatArgsSchema = ChatIdOnlyArgsSchema;
export type WaUnpinChatArgs = z.infer<typeof WaUnpinChatArgsSchema>;
export const WaUnpinChatResultSchema = ChatMutationResultEnvelopeSchema;
export type WaUnpinChatResult = z.infer<typeof WaUnpinChatResultSchema>;

/** `unmuteDate` follows the chat-tools.ts wrapper: ISO string when caller
 *  passed a Date; raw `string | number | null` passthrough otherwise.
 *  Undefined means "mute forever" — engine reads `data.unmuteDate`
 *  directly (PageMessageService line 761). */
export const WaMuteChatArgsSchema = z.object({
  chatId: WireJidSchema,
  /** Absent or `null` = mute forever, on both engines. */
  unmuteDate: z.union([z.string(), z.number(), z.null()]).optional(),
});
export type WaMuteChatArgs = z.infer<typeof WaMuteChatArgsSchema>;
/** `muteChat` returns the wwebjs Chat mute state: `{isMuted, muteExpiration}`.
 *  Same shape for `unmuteChat`. */
const MuteStateSchema = z.object({
  isMuted: z.boolean(),
  /** Unix seconds; `-1` = forever. */
  muteExpiration: z.number(),
});
export const WaMuteChatResultSchema = MuteStateSchema;
export type WaMuteChatResult = z.infer<typeof WaMuteChatResultSchema>;

export const WaUnmuteChatArgsSchema = ChatIdOnlyArgsSchema;
export type WaUnmuteChatArgs = z.infer<typeof WaUnmuteChatArgsSchema>;
export const WaUnmuteChatResultSchema = MuteStateSchema;
export type WaUnmuteChatResult = z.infer<typeof WaUnmuteChatResultSchema>;

export const WaMarkChatUnreadArgsSchema = ChatIdOnlyArgsSchema;
export type WaMarkChatUnreadArgs = z.infer<typeof WaMarkChatUnreadArgsSchema>;
export const WaMarkChatUnreadResultSchema = ChatMutationResultEnvelopeSchema;
export type WaMarkChatUnreadResult = z.infer<typeof WaMarkChatUnreadResultSchema>;

export const WaSendStateTypingArgsSchema = ChatIdOnlyArgsSchema;
export type WaSendStateTypingArgs = z.infer<typeof WaSendStateTypingArgsSchema>;
export const WaSendStateTypingResultSchema = ChatMutationResultEnvelopeSchema;
export type WaSendStateTypingResult = z.infer<typeof WaSendStateTypingResultSchema>;

export const WaSendStateRecordingArgsSchema = ChatIdOnlyArgsSchema;
export type WaSendStateRecordingArgs = z.infer<typeof WaSendStateRecordingArgsSchema>;
export const WaSendStateRecordingResultSchema = ChatMutationResultEnvelopeSchema;
export type WaSendStateRecordingResult = z.infer<typeof WaSendStateRecordingResultSchema>;

export const WaClearStateArgsSchema = ChatIdOnlyArgsSchema;
export type WaClearStateArgs = z.infer<typeof WaClearStateArgsSchema>;
export const WaClearStateResultSchema = ChatMutationResultEnvelopeSchema;
export type WaClearStateResult = z.infer<typeof WaClearStateResultSchema>;


// ── Chat labels ─────────────────────────────────────────────────────

export const WaGetChatLabelsArgsSchema = ChatIdOnlyArgsSchema;
export type WaGetChatLabelsArgs = z.infer<typeof WaGetChatLabelsArgsSchema>;
/** Label rows (BtSlimLabel). Runtime-lenient: today the extension emits the
 *  WAWeb label model (`id`, not `labelId`), so every field is optional and
 *  extras pass through. @5.0b strict BtSlimLabelSchema rows. */
const LenientLabelRowsSchema = z.array(BtSlimLabelSchema.partial().passthrough());

/** A chat's labels. */
export const WaGetChatLabelsResultSchema = z.object({
  // BE#509 (same class): no labels → engine omits `labels` → default to [].
  labels: LenientLabelRowsSchema.default([]),
}).strict();
export type WaGetChatLabelsResult = z.infer<typeof WaGetChatLabelsResultSchema>;

export const WaChangeLabelsArgsSchema = z.object({
  chatId: WireJidSchema,
  labelIds: z.array(z.union([z.number(), z.string()])).optional(),
});
export type WaChangeLabelsArgs = z.infer<typeof WaChangeLabelsArgsSchema>;
/** Page handler returns the chat-mutation envelope. WhatsApp-Business-only
 *  feature — on non-business accounts the underlying WAWeb call throws
 *  (`addOrRemoveLabels is not a function`); the handler catches that class
 *  of failure and surfaces it as `{result: false, error: 'wa_business_required'}`
 *  so callers can distinguish "feature unavailable on this account" from
 *  "operation failed mid-flight". */
export const WaChangeLabelsResultSchema = ChatMutationResultEnvelopeSchema;
export type WaChangeLabelsResult = z.infer<typeof WaChangeLabelsResultSchema>;

/** List ALL WhatsApp Business labels on the connected account. No args. */
export const WaGetLabelsArgsSchema = z.object({}).strict();
export type WaGetLabelsArgs = z.infer<typeof WaGetLabelsArgsSchema>;
/** Every label on the account. Same rows as `WaGetChatLabelsResultSchema`:
 *  no labels → engine omits `labels` → default to []. WhatsApp-Business-only
 *  feature; on a non-business account the engine handler returns an empty list. */
export const WaGetLabelsResultSchema = z.object({
  labels: LenientLabelRowsSchema.default([]),
}).strict();
export type WaGetLabelsResult = z.infer<typeof WaGetLabelsResultSchema>;

// ── Chat pinned-messages ────────────────────────────────────────────

export const WaGetPinnedMessagesArgsSchema = ChatIdOnlyArgsSchema;
export type WaGetPinnedMessagesArgs = z.infer<typeof WaGetPinnedMessagesArgsSchema>;
/** The pinned messages as message rows, so their identity (key, from/author,
 *  quoted key, reactions) is declared and mapped. Rows are runtime-lenient:
 *  today the extension emits its fat lean row and the pod a `{id, chatId}` stub
 *  for an evicted message, so every field is optional and extras pass through.
 *  @5.0b strict BtSlimMessageSchema rows. */
export const WaGetPinnedMessagesResultSchema = z.object({
  // BE#509 (same class): no pinned messages → engine omits `messages` → default to [].
  messages: z.array(BtSlimMessageSchema.partial().passthrough()).default([]),
}).strict();
export type WaGetPinnedMessagesResult = z.infer<typeof WaGetPinnedMessagesResultSchema>;

// ── Customer note ───────────────────────────────────────────────────

export const WaAddOrEditCustomerNoteArgsSchema = z.object({
  chatId: WireJidSchema,
  note: z.string(),
});
export type WaAddOrEditCustomerNoteArgs = z.infer<typeof WaAddOrEditCustomerNoteArgsSchema>;
/** Page handler returns the chat-mutation envelope. WhatsApp-Business-only
 *  feature — on non-business accounts the underlying WAWeb call throws
 *  (`smbNotesV1Enabled is not a function`); the handler catches that
 *  class of failure and surfaces it as `{result: false, error: 'wa_business_required'}`
 *  so callers can distinguish "feature unavailable on this account" from
 *  "operation failed mid-flight". */
export const WaAddOrEditCustomerNoteResultSchema = ChatMutationResultEnvelopeSchema;
export type WaAddOrEditCustomerNoteResult = z.infer<typeof WaAddOrEditCustomerNoteResultSchema>;

// ── Labels (extension-only label-filter management) ────────────────
//
// Wire payload IS the chats-to-show map (or filter directive), passed
// straight as `data` — no `{chats: ...}` wrap. Per chat-tools-actions
// in wa-tools-client-actions.ts line 318. Page handler is void: the
// `handleUpdateShouldAppearChatsByList` impl mutates the WA UI and
// returns `undefined`; wireHandler calls `callback(undefined)`.

export const WaUpdateShouldAppearChatsByListArgsSchema = z.union([
  z.record(z.boolean()),
  z.object({
    filterType: z.string(),
    excludeIds: z.array(z.string()),
  }).strict(),
  z.null(),
]);
export type WaUpdateShouldAppearChatsByListArgs = z.infer<typeof WaUpdateShouldAppearChatsByListArgsSchema>;
/** #770: an allowlist (label) filter replies with the label's member breakdown so the
 *  label bar can explain an empty list; reset and exclude filters still reply
 *  `undefined`, which `z.void()` accepts. `noConversationIds` are the members with no
 *  chat history (not archived, no `t`). */
export const WaLabelViewSnapshotSchema = z.object({
  stats: z.object({
    total: z.number().int().nonnegative(),
    visible: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    noConversation: z.number().int().nonnegative(),
  }),
  noConversationIds: z.array(z.string()),
});
export type WaLabelViewSnapshot = z.infer<typeof WaLabelViewSnapshotSchema>;
export const WaUpdateShouldAppearChatsByListResultSchema = z.union([WaLabelViewSnapshotSchema, z.void()]);
export type WaUpdateShouldAppearChatsByListResult = z.infer<typeof WaUpdateShouldAppearChatsByListResultSchema>;

// ── Session (validateStores / triggerQRTimeout) ────────────────────

export const WaValidateStoresArgsSchema = z.object({}).strict();
export type WaValidateStoresArgs = z.infer<typeof WaValidateStoresArgsSchema>;
/** Page handler emits `{success, missingModules}` (PageMessageService
 *  line 723-726). Note: the *thunk* dispatches to the legacy string
 *  cmd `"validateStores"` rather than this WA_TOOL_COMMANDS literal,
 *  so this contract entry currently has no wire traffic in chrome —
 *  it documents the canonical shape for future engines. Fields are
 *  optional so the legacy-cmd unwrap path (which loses the shape via
 *  `unknown`) still type-checks. */
export const WaValidateStoresResultSchema = z.object({
  success: z.boolean().optional(),
  missingModules: z.array(z.string()).optional(),
}).strict();
export type WaValidateStoresResult = z.infer<typeof WaValidateStoresResultSchema>;

export const WaTriggerQRTimeoutArgsSchema = z.object({}).strict();
export type WaTriggerQRTimeoutArgs = z.infer<typeof WaTriggerQRTimeoutArgsSchema>;
/** Page handler emits `void` today (the chrome client-action returns
 *  `Promise<void>`); the aspirational `{success, action}` envelope is
 *  not actually produced. Modeled as a fully-optional bag so both the
 *  current void emit and a future structured emit type-check. */
export const WaTriggerQRTimeoutResultSchema = z.object({
  success: z.boolean().optional(),
  action: z.string().optional(),
}).strict();
export type WaTriggerQRTimeoutResult = z.infer<typeof WaTriggerQRTimeoutResultSchema>;

// ── Status (status/story posting) ──────────────────────────────────

export const CreateStatusArgsSchema = z.object({
  supportedUserId: z.string().optional(),
  /** FE#360 — scheduled `userMessage._id`; stamped into the posted Status's
   *  messageSecret so a re-dispatch can recognise it and skip re-posting. */
  userMessageId: z.string().optional(),
  text: z.string().optional(),
  content: z.string().optional(),
  fileDataUrl: z.string().optional(),
  fileName: z.string().optional(),
  fileMimeType: z.string().optional(),
});
export type CreateStatusArgs = z.infer<typeof CreateStatusArgsSchema>;
/** `createStatus` page handler returns `{success, action?, messageId?,
 *  error?}`; the content-script thunk strips `success`/`error` into the
 *  outer WaToolResponse envelope, so the wire payload is just
 *  `{action?, messageId?}`. `success` is modeled optional to admit both
 *  the page-handler raw shape and the post-thunk wire shape — the outer
 *  envelope is the canonical success signal. */
export const CreateStatusResultSchema = z.object({
  success: z.boolean().optional(),
  action: z.enum(['text', 'media']).optional(),
  messageId: z.string().optional(),
  error: z.string().optional(),
}).strict();
export type CreateStatusResult = z.infer<typeof CreateStatusResultSchema>;

// ── Message-store lookup (unlogged-WA-key) ─────────────────────────

/** One of the newest chat messages in a store lookup's debug info. Never carries the body or caption (#471):
 *  No fingerprint either: these are other messages in the chat, guessable offline. `body`/`caption` are optional only so rows and
 *  results written before #471 still parse; nothing writes them any more. */
export const StoreLookupTopMessageSchema = z.object({
  id: z.string(),
  t: z.number().optional(),
  ack: z.number().optional(),
  fromMe: z.boolean().optional(),
  body: z.string().optional(),
  caption: z.string().optional(),
});
export type StoreLookupTopMessage = z.infer<typeof StoreLookupTopMessageSchema>;

export const GetUnloggedWaMessageKeyIfExistInStoreArgsSchema = z.object({
  chatId: WireJidSchema,
  userMessageId: z.string(),
  /** `dueDate` is materialised as a Date in the notifier handler before
   *  forwarding, but on the wire (and as the page handler's typed param)
   *  it's a Date object. zod has no first-class Date wire type — model
   *  as `z.date()` which accepts Date instances. */
  dueDate: z.date(),
});
export type GetUnloggedWaMessageKeyIfExistInStoreArgs = z.infer<typeof GetUnloggedWaMessageKeyIfExistInStoreArgsSchema>;
/** Page handler returns `{waMessageKey: string|null, debugInfo: {...}}`
 *  from `getUnloggedWaMessageKeyIfExistInStore`. */
export const GetUnloggedWaMessageKeyIfExistInStoreResultSchema = z.object({
  waMessageKey: WireKeySchema.nullable(),
  /** wa-js Message.ack of the matched stored copy (`>= 1` = WhatsApp server
   *  received it; `< 1` = PENDING/ERROR, not really sent). Optional for back-compat
   *  with engines/handlers that don't surface it. */
  ack: z.number().optional(),
  /** #458 evidence about the matched copy (never its body): id shape, timestamp, fromMe and
   *  a transient salted body fingerprint. `bodyFp` crosses the page→content boundary IN MEMORY only so
   *  the caller can compare it with the sent text; it is never persisted or logged. Absent when
   *  nothing matched. */
  match: z.object({
    idFormat: z.string(),
    t: z.number().optional(),
    fromMe: z.boolean().optional(),
    bodyFp: z.string().optional(),
    /** #503 matched Msg `errorCode` (SendFailureErrorCode enum string); absent when unset. */
    errorCode: z.string().optional(),
    /** #503 matched Msg `isSendFailure` (WA Web's send path threw for this message). */
    isSendFailure: z.boolean().optional(),
  }).optional(),
  debugInfo: z.object({
    totalMessagesFetched: z.number(),
    top5Messages: z.array(StoreLookupTopMessageSchema),
    searchMethod: z.enum(['global-store', 'fetch-fallback']).optional(),
    globalStoreSearched: z.number().optional(),
  }),
}).strict();
export type GetUnloggedWaMessageKeyIfExistInStoreResult = z.infer<typeof GetUnloggedWaMessageKeyIfExistInStoreResultSchema>;

// ── LID → c.us conversion ──────────────────────────────────────────

export const ConvertWaLidSerializedToCusSerializedArgsSchema = z.object({
  waLidSerialized: z.string(),
});
export type ConvertWaLidSerializedToCusSerializedArgs = z.infer<typeof ConvertWaLidSerializedToCusSerializedArgsSchema>;
/** `{convertedId}`: on a miss the engine returns its input. The inner `success`
 *  is gone (it duplicated the envelope); not `.strict()`, so an older engine's
 *  `success` is stripped instead of failing the parse. @5.0b re-add `.strict()`. */
export const ConvertWaLidSerializedToCusSerializedResultSchema = z.object({
  convertedId: WireJidSchema.optional(),
});
export type ConvertWaLidSerializedToCusSerializedResult = z.infer<typeof ConvertWaLidSerializedToCusSerializedResultSchema>;

// ── c.us → LID resolution (BE#676 lazy @lid rekey) ─────────────────
// Reverse of the above. Resolve a phone id (`<digits>@c.us`) to its
// user-level `<lid>@lid` via the engine's pn→lid mapping (Baileys
// `lidMapping.getLIDForPN`). `lid` is NULLABLE so the caller can tell a
// definitive "no mapping" (engine answered, `lid: null`) apart from a
// transient engine fault (the dispatch throws 4xx/5xx — handled upstream).

export const ConvertWaCusSerializedToWaLidSerializedArgsSchema = z.object({
  cusSerialized: z.string(),
});
export type ConvertWaCusSerializedToWaLidSerializedArgs = z.infer<typeof ConvertWaCusSerializedToWaLidSerializedArgsSchema>;
export const ConvertWaCusSerializedToWaLidSerializedResultSchema = z.object({
  success: z.boolean(),
  lid: z.string().nullable(),
}).strict();
export type ConvertWaCusSerializedToWaLidSerializedResult = z.infer<typeof ConvertWaCusSerializedToWaLidSerializedResultSchema>;

// ── Utility (PING / currentDateTime) ───────────────────────────────

export const PingArgsSchema = z.object({}).strict();
export type PingArgs = z.infer<typeof PingArgsSchema>;
/** `{message:'pong', timestamp:<ISO>, engineType, clientId?}` — the extension
 *  notifier's PING case (`wa-tools-notifier-handlers.ts`) and the Baileys pod
 *  (`index.ts`, which also reports its MQTT `clientId`). */
export const PingResultSchema = z.object({
  message: z.string(),
  timestamp: z.string(),
  engineType: z.string(),
  clientId: z.string().optional(),
}).strict();
export type PingResult = z.infer<typeof PingResultSchema>;

export const CurrentDateTimeArgsSchema = z.object({}).strict();
export type CurrentDateTimeArgs = z.infer<typeof CurrentDateTimeArgsSchema>;
/** Notifier emits the engine-resolved tz snapshot used by the MCP
 *  backend for localDueDateParts → UTC conversion. */
export const CurrentDateTimeResultSchema = z.object({
  tz: z.string(),
  iso_local: z.string(),
  iso_utc: z.string(),
  epoch_ms: z.number(),
  offset_minutes: z.number(),
  dst: z.boolean(),
  source: z.enum(['extension', 'gateway']),
}).strict();
export type CurrentDateTimeResult = z.infer<typeof CurrentDateTimeResultSchema>;

// ── Account health: restriction + send quota ───────────────────────
//
// WhatsApp restricts accounts that reach out to too many people who have never
// replied ("reachout timelock"), and refuses the send with ack error 463. Both
// engines can now read that state directly instead of inferring it from failures:
// the Baileys pod over MEX, the Chrome extension out of WA Web's user-prefs cache
// (`WAReachoutTimelockState` / `WANewChatMessageCappingData`). The GraphQL doc ids
// are identical on both paths, so the reported shape is the same.
//
// `status` exists to keep "definitely not restricted" distinct from "could not
// ask". Collapsing those is the bug that would matter: a probe failure read as an
// all-clear silently clears a real restriction, and the customer goes back to
// being told their sends failed because no engine was connected.

export const AccountRestrictionSchema = z.object({
  isActive: z.boolean(),
  /** ISO-8601 when the restriction lifts; null when WhatsApp gave no window. */
  timeEnforcementEnds: z.string().nullable(),
  /** e.g. RESTRICT_ALL_COMPANIONS, WEB_COMPANION_ONLY, BIZ_QUALITY. */
  enforcementType: z.string().nullable(),
}).strict();
export type AccountRestriction = z.infer<typeof AccountRestrictionSchema>;

export const AccountQuotaSchema = z.object({
  total: z.number().nullable(),
  used: z.number().nullable(),
  /** 0-100, derived from used/total so it can never disagree with them. */
  usagePercent: z.number().nullable(),
  cycleStart: z.string().nullable(),
  cycleEnd: z.string().nullable(),
  /** NONE | FIRST_WARNING | SECOND_WARNING | CAPPED. */
  status: z.string().nullable(),
  /** Capping-programme eligibility: NOT_ELIGIBLE | NOT_ACTIVE | ACTIVE | ACTIVE_UPGRADE_AVAILABLE.
   *  Without these, "not enrolled" and "enrolled but unused" are indistinguishable — both are
   *  NONE/0/0. Measured 2026-07-29: the whole Baileys fleet reports NOT_ELIGIBLE. */
  mvStatus: z.string().nullable(),
  oteStatus: z.string().nullable(),
}).strict();
export type AccountQuota = z.infer<typeof AccountQuotaSchema>;

export const WaAccountRestrictionArgsSchema = z.object({
  /** Pull from WhatsApp before reading the cache. Costs one MEX round-trip. */
  refresh: z.boolean().optional(),
}).strict();
export type WaAccountRestrictionArgs = z.infer<typeof WaAccountRestrictionArgsSchema>;

export const WaAccountRestrictionResultSchema = z.object({
  /** 'unavailable' ⇒ WA's modules were unreachable; restriction/quota below are
   *  NOT evidence of anything and must not be persisted. */
  status: z.enum(['ok', 'unavailable']),
  /** null ⇒ no active restriction (only meaningful when status === 'ok'). */
  restriction: AccountRestrictionSchema.nullable(),
  /** null ⇒ WhatsApp does not publish a quota for this account (the capping
   *  gate is off for most), NOT "quota exhausted". */
  quota: AccountQuotaSchema.nullable(),
  probedAt: z.string(),
}).strict();
export type WaAccountRestrictionResult = z.infer<typeof WaAccountRestrictionResultSchema>;

export const WaChatSendRiskArgsSchema = z.object({
  chatId: WireJidSchema,
}).strict();
export type WaChatSendRiskArgs = z.infer<typeof WaChatSendRiskArgsSchema>;

/** Every input to the verdict is returned alongside it — a bare `blocked: true`
 *  is undiagnosable in a fleet-wide scan, which is the whole reason this exists. */
export const WaChatSendRiskResultSchema = z.object({
  status: z.enum(['ok', 'unavailable']),
  blocked: z.boolean(),
  reason: z.string().nullable(),
  timelocked: z.boolean(),
  hasTcToken: z.boolean(),
  isUser: z.boolean(),
  isMe: z.boolean(),
  isEnterprise: z.boolean(),
  isSupport: z.boolean(),
  isBot: z.boolean(),
}).strict();
export type WaChatSendRiskResult = z.infer<typeof WaChatSendRiskResultSchema>;

// ── Cross-engine scheduling protocol ───────────────────────────────
//
// Backend scheduler dispatcher calls into the engine: "do you already
// have this user-message? if not, insert+send it." Body shape is bt-
// common's `InsertOnlyIfMissingRequest`; response is
// `InsertOnlyIfMissingResponse`. Both modeled here as the source of
// truth for the engine-dispatch boundary parse.

export const InsertOnlyIfMissingArgsSchema = z.object({
  /** Full UserMessage doc — opaque on the contract boundary; the
   *  engine deserialises it via its own UserMessage parser. */
  message: z.unknown(),
  /** Sentinel — distinguishes from a plain send-now command. */
  checkStore: z.literal(true),
}).strict();
export type InsertOnlyIfMissingArgs = z.infer<typeof InsertOnlyIfMissingArgsSchema>;

/** Both engines reply BEFORE sending (the send then runs through
 *  runSendProtocol). A refusal throws `EngineToolError`; there is no inner
 *  `success`/`error`. */
export const InsertOnlyIfMissingResultSchema = z.object({
  /** The engine already has (sent) this user-message. */
  messageExists: z.boolean(),
  /** The engine's WhatsApp connection state (the backend classifier persists it). */
  whatsappState: z.string(),
}).strict();
export type InsertOnlyIfMissingResult = z.infer<typeof InsertOnlyIfMissingResultSchema>;

// --- Registry: command literal -> {Args, Result}. Add commands as their
// shape gets pinned down. Untyped commands fall through to `unknown` via
// the helpers below.

export interface WaToolContractMap {
  // --- Groups ---
  [WA_TOOL_COMMANDS.waCreateGroupTool]: {
    Args: WaCreateGroupArgs;
    Result: WaCreateGroupResult;
  };
  [WA_TOOL_COMMANDS.waGetGroupDataTool]: {
    Args: WaGetGroupDataArgs;
    Result: WaGetGroupDataResult;
  };
  [WA_TOOL_COMMANDS.waUpdateGroupNameTool]: {
    Args: WaUpdateGroupNameArgs;
    Result: WaUpdateGroupNameResult;
  };
  [WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool]: {
    Args: WaUpdateGroupDescriptionArgs;
    Result: WaUpdateGroupDescriptionResult;
  };
  [WA_TOOL_COMMANDS.waAddGroupParticipantTool]: {
    Args: WaAddGroupParticipantArgs;
    Result: WaAddGroupParticipantResult;
  };
  [WA_TOOL_COMMANDS.waRemoveGroupParticipantTool]: {
    Args: WaRemoveGroupParticipantArgs;
    Result: WaRemoveGroupParticipantResult;
  };
  [WA_TOOL_COMMANDS.waSetGroupAdminRightsTool]: {
    Args: WaSetGroupAdminRightsArgs;
    Result: WaSetGroupAdminRightsResult;
  };
  [WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool]: {
    Args: WaRemoveGroupAdminRightsArgs;
    Result: WaRemoveGroupAdminRightsResult;
  };
  [WA_TOOL_COMMANDS.waSetGroupPictureTool]: {
    Args: WaSetGroupPictureArgs;
    Result: WaSetGroupPictureResult;
  };
  [WA_TOOL_COMMANDS.waLeaveGroupTool]: {
    Args: WaLeaveGroupArgs;
    Result: WaLeaveGroupResult;
  };

  // --- Contacts ---
  [WA_TOOL_COMMANDS.waGetProfilePicUrlTool]: {
    Args: WaGetProfilePicUrlArgs;
    Result: WaGetProfilePicUrlResult;
  };

  // --- Groups (lean read) ---

  // --- Newsletters ---
  [WA_TOOL_COMMANDS.waCreateNewsletterTool]: {
    Args: WaCreateNewsletterArgs;
    Result: WaCreateNewsletterResult;
  };
  [WA_TOOL_COMMANDS.waGetNewsletterTool]: {
    Args: WaGetNewsletterArgs;
    Result: WaGetNewsletterResult;
  };

  // --- Engine / Utils ---
  [WA_TOOL_COMMANDS.getWaMeInfoTool]: {
    Args: GetWaMeInfoArgs;
    Result: GetWaMeInfoResult;
  };
  [WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool]: {
    Args: IsWhatsAppConnectedArgs;
    Result: IsWhatsAppConnectedResult;
  };
  [WA_TOOL_COMMANDS.waReloadPageTool]: {
    Args: WaReloadPageArgs;
    Result: WaReloadPageResult;
  };
  [WA_TOOL_COMMANDS.waGuardedReloadTool]: {
    Args: WaGuardedReloadArgs;
    Result: WaGuardedReloadResult;
  };
  [WA_TOOL_COMMANDS.waTriggerLogoutTool]: {
    Args: WaTriggerLogoutArgs;
    Result: WaTriggerLogoutResult;
  };
  [WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool]: {
    Args: WaValidatePhoneNumberOrChatIdArgs;
    Result: WaValidatePhoneNumberOrChatIdResult;
  };
  [WA_TOOL_COMMANDS.waGetLinkPreviewTool]: {
    Args: WaGetLinkPreviewArgs;
    Result: WaGetLinkPreviewResult;
  };

  // --- Chats / Messages (engine-side) ---
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: {
    Args: WaQueryMessagesArgs;
    Result: WaQueryMessagesResult;
  };
  [WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool]: {
    Args: WaGetChatObjectByChatIdArgs;
    Result: WaGetChatObjectByChatIdResult;
  };
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: {
    Args: WaQueryContactsAndChatsArgs;
    Result: WaQueryContactsAndChatsResult;
  };
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: {
    Args: WaGetLatestChatsAndMessagesArgs;
    Result: WaGetLatestChatsAndMessagesResult;
  };
  [WA_TOOL_COMMANDS.waQueryVaultMessagesTool]: {
    Args: WaQueryVaultMessagesArgs;
    Result: WaQueryVaultMessagesResult;
  };
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: {
    Args: WaGetChatParticipantsArgs;
    Result: WaGetChatParticipantsResult;
  };
  [WA_TOOL_COMMANDS.waGetCommonGroupsTool]: {
    Args: WaGetCommonGroupsArgs;
    Result: WaGetCommonGroupsResult;
  };
  [WA_TOOL_COMMANDS.waSendSeenTool]: {
    Args: WaSendSeenArgs;
    Result: WaSendSeenResult;
  };
  [WA_TOOL_COMMANDS.waOpenChatTool]: {
    Args: WaOpenChatArgs;
    Result: WaOpenChatResult;
  };
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: {
    Args: WaLoadMessagesFromPhoneArgs;
    Result: WaLoadMessagesFromPhoneResult;
  };
  [WA_TOOL_COMMANDS.waGetMessageByKeyTool]: {
    Args: WaGetMessageByKeyArgs;
    Result: WaGetMessageByKeyResult;
  };
  [WA_TOOL_COMMANDS.waGetMessageAckTool]: {
    Args: WaGetMessageAckArgs;
    Result: WaGetMessageAckResult;
  };
  [WA_TOOL_COMMANDS.waGetPollResultsTool]: {
    Args: WaGetPollResultsArgs;
    Result: WaGetPollResultsResult;
  };
  [WA_TOOL_COMMANDS.waGetMessagesAckDataTool]: {
    Args: WaGetMessagesAckDataArgs;
    Result: WaGetMessagesAckDataResult;
  };
  [WA_TOOL_COMMANDS.waGetMessageMediaTool]: {
    Args: WaGetMessageMediaArgs;
    Result: WaGetMessageMediaResult;
  };
  [WA_TOOL_COMMANDS.waReactToMessageThunkTool]: {
    Args: WaReactToMessageArgs;
    Result: WaReactToMessageResult;
  };
  [WA_TOOL_COMMANDS.waForwardMessageTool]: {
    Args: WaForwardMessageArgs;
    Result: WaForwardMessageResult;
  };
  [WA_TOOL_COMMANDS.waStarMessageTool]: {
    Args: WaStarMessageArgs;
    Result: WaStarMessageResult;
  };
  [WA_TOOL_COMMANDS.waDeleteMessageTool]: {
    Args: WaDeleteMessageArgs;
    Result: WaDeleteMessageResult;
  };
  [WA_TOOL_COMMANDS.waPinMessageTool]: {
    Args: WaPinMessageArgs;
    Result: WaPinMessageResult;
  };
  [WA_TOOL_COMMANDS.waUnpinMessageTool]: {
    Args: WaUnpinMessageArgs;
    Result: WaUnpinMessageResult;
  };
  [WA_TOOL_COMMANDS.sendMessageThunkTool]: {
    Args: WaSendMessageThunkArgs;
    Result: WaSendMessageThunkResult;
  };

  // --- Chat mutations ---
  [WA_TOOL_COMMANDS.waClearMessagesTool]: {
    Args: WaClearMessagesArgs;
    Result: WaClearMessagesResult;
  };
  [WA_TOOL_COMMANDS.waDeleteChatTool]: {
    Args: WaDeleteChatArgs;
    Result: WaDeleteChatResult;
  };
  [WA_TOOL_COMMANDS.waArchiveChatTool]: {
    Args: WaArchiveChatArgs;
    Result: WaArchiveChatResult;
  };
  [WA_TOOL_COMMANDS.waUnarchiveChatTool]: {
    Args: WaUnarchiveChatArgs;
    Result: WaUnarchiveChatResult;
  };
  [WA_TOOL_COMMANDS.waPinChatTool]: {
    Args: WaPinChatArgs;
    Result: WaPinChatResult;
  };
  [WA_TOOL_COMMANDS.waUnpinChatTool]: {
    Args: WaUnpinChatArgs;
    Result: WaUnpinChatResult;
  };
  [WA_TOOL_COMMANDS.waMuteChatTool]: {
    Args: WaMuteChatArgs;
    Result: WaMuteChatResult;
  };
  [WA_TOOL_COMMANDS.waUnmuteChatTool]: {
    Args: WaUnmuteChatArgs;
    Result: WaUnmuteChatResult;
  };
  [WA_TOOL_COMMANDS.waMarkChatUnreadTool]: {
    Args: WaMarkChatUnreadArgs;
    Result: WaMarkChatUnreadResult;
  };
  [WA_TOOL_COMMANDS.waSendStateTypingTool]: {
    Args: WaSendStateTypingArgs;
    Result: WaSendStateTypingResult;
  };
  [WA_TOOL_COMMANDS.waSendStateRecordingTool]: {
    Args: WaSendStateRecordingArgs;
    Result: WaSendStateRecordingResult;
  };
  [WA_TOOL_COMMANDS.waClearStateTool]: {
    Args: WaClearStateArgs;
    Result: WaClearStateResult;
  };

  // --- Chat labels / pinned / customer notes ---
  [WA_TOOL_COMMANDS.waGetChatLabelsTool]: {
    Args: WaGetChatLabelsArgs;
    Result: WaGetChatLabelsResult;
  };
  [WA_TOOL_COMMANDS.waChangeLabelsTool]: {
    Args: WaChangeLabelsArgs;
    Result: WaChangeLabelsResult;
  };
  [WA_TOOL_COMMANDS.waGetLabelsTool]: {
    Args: WaGetLabelsArgs;
    Result: WaGetLabelsResult;
  };
  [WA_TOOL_COMMANDS.waGetPinnedMessagesTool]: {
    Args: WaGetPinnedMessagesArgs;
    Result: WaGetPinnedMessagesResult;
  };
  [WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool]: {
    Args: WaAddOrEditCustomerNoteArgs;
    Result: WaAddOrEditCustomerNoteResult;
  };

  // --- Labels (extension-only filter) ---
  [WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool]: {
    Args: WaUpdateShouldAppearChatsByListArgs;
    Result: WaUpdateShouldAppearChatsByListResult;
  };

  // --- Session / store validation ---
  [WA_TOOL_COMMANDS.waValidateStoresTool]: {
    Args: WaValidateStoresArgs;
    Result: WaValidateStoresResult;
  };
  [WA_TOOL_COMMANDS.waTriggerQRTimeoutTool]: {
    Args: WaTriggerQRTimeoutArgs;
    Result: WaTriggerQRTimeoutResult;
  };

  // --- Status (story) ---
  [WA_TOOL_COMMANDS.createStatusTool]: {
    Args: CreateStatusArgs;
    Result: CreateStatusResult;
  };

  // --- Message-store lookup / LID conversion ---
  [WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE]: {
    Args: GetUnloggedWaMessageKeyIfExistInStoreArgs;
    Result: GetUnloggedWaMessageKeyIfExistInStoreResult;
  };
  [WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED]: {
    Args: ConvertWaLidSerializedToCusSerializedArgs;
    Result: ConvertWaLidSerializedToCusSerializedResult;
  };
  [WA_TOOL_COMMANDS.CONVERT_WA_CUS_SERIALIZED_TO_WA_LID_SERIALIZED]: {
    Args: ConvertWaCusSerializedToWaLidSerializedArgs;
    Result: ConvertWaCusSerializedToWaLidSerializedResult;
  };

  // --- Utility ---
  [WA_TOOL_COMMANDS.PING]: {
    Args: PingArgs;
    Result: PingResult;
  };
  [WA_TOOL_COMMANDS.currentDateTimeTool]: {
    Args: CurrentDateTimeArgs;
    Result: CurrentDateTimeResult;
  };

  // --- Account health ---
  [WA_TOOL_COMMANDS.waAccountRestrictionTool]: {
    Args: WaAccountRestrictionArgs;
    Result: WaAccountRestrictionResult;
  };
  [WA_TOOL_COMMANDS.waChatSendRiskTool]: {
    Args: WaChatSendRiskArgs;
    Result: WaChatSendRiskResult;
  };

  // --- Cross-engine scheduling ---
  [WA_TOOL_COMMANDS.insertOnlyIfMissing]: {
    Args: InsertOnlyIfMissingArgs;
    Result: InsertOnlyIfMissingResult;
  };
}

// Strict union of contract keys. The `| string` escape hatch was
// dropped because it widened C in the typed-overload of
// PageMessageService.registerToolHandler<C extends WaToolCommand> to
// `string` — defeating literal inference and collapsing
// WaToolArgs<C> to `unknown`. Now any unknown command literal fails
// the constraint at compile time, surfacing missing contract entries.
export type WaToolCommand = keyof WaToolContractMap;
export type WaToolArgs<C extends WaToolCommand> = C extends keyof WaToolContractMap
  ? WaToolContractMap[C]['Args']
  : unknown;
export type WaToolResult<C extends WaToolCommand> = C extends keyof WaToolContractMap
  ? WaToolContractMap[C]['Result']
  : unknown;

// Strict contract aliases — unlike WaToolArgs/WaToolResult above (which
// widen to `unknown` outside the map), these stay keyed exactly to the
// contract map so the backend's callEngineTool<C> compile-checks its args
// and returned data against the canonical Args/Result.
export type WaContract = keyof WaToolContractMap;
export type ArgsOf<C extends WaContract> = WaToolContractMap[C]['Args'];
export type ResultOf<C extends WaContract> = WaToolContractMap[C]['Result'];

// ── Shared IPC/RPC dispatcher helpers ────────────────────────────────
//
// Both sides of every WhatsApp boundary (extension's
// content-script→page postMessageWithResponse, backend's
// MQTT-backed callEngineTool) need the same conditional-type trick:
// when `cmd` resolves to a WaToolCommand literal, `args` must satisfy
// WaToolArgs<C>; otherwise the legacy escape hatch passes through.
//
// Defining the discriminant types here keeps both dispatchers in
// lockstep: adding a contract entry to WaToolContractMap immediately
// tightens BOTH the extension-side and backend-side callsites — drift
// surfaces at tsc, not at runtime via 422 "field is required" errors.
//
// The per-side result-wrapping (WireResponse<...> | undefined for the
// extension's content-script seam vs unwrapped data for backend's
// post-error-mapping engine return) lives in each dispatcher.
export type WaArgsFor<C extends string> =
  C extends WaToolCommand ? WaToolArgs<C> : unknown;
export type WaResultFor<C extends string> =
  C extends WaToolCommand ? WaToolResult<C> : unknown;

// ── Runtime Result-schema lookup ─────────────────────────────────────
//
// Per-command zod Result schema. `callEngineTool` looks the schema up
// here and `.safeParse()` the engine response at the dispatch boundary
// (Layer 2 of the bottom-up architecture arc, #50). Service code does
// NOT reach for per-tool *_ResultSchema constants — the boundary parse
// is centralized.
//
// `satisfies` (not `:`) keeps each command's schema TYPED to its actual
// Result schema constant rather than collapsed to z.ZodTypeAny — so
// future schema changes propagate without weakening the map type.

export const WA_TOOL_RESULT_SCHEMAS = {
  [WA_TOOL_COMMANDS.waCreateGroupTool]: WaCreateGroupResultSchema,
  [WA_TOOL_COMMANDS.waGetGroupDataTool]: WaGetGroupDataResultSchema,
  [WA_TOOL_COMMANDS.waUpdateGroupNameTool]: WaUpdateGroupNameResultSchema,
  [WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool]: WaUpdateGroupDescriptionResultSchema,
  [WA_TOOL_COMMANDS.waAddGroupParticipantTool]: WaAddGroupParticipantResultSchema,
  [WA_TOOL_COMMANDS.waRemoveGroupParticipantTool]: WaRemoveGroupParticipantResultSchema,
  [WA_TOOL_COMMANDS.waSetGroupAdminRightsTool]: WaSetGroupAdminRightsResultSchema,
  [WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool]: WaRemoveGroupAdminRightsResultSchema,
  [WA_TOOL_COMMANDS.waSetGroupPictureTool]: WaSetGroupPictureResultSchema,
  [WA_TOOL_COMMANDS.waLeaveGroupTool]: WaLeaveGroupResultSchema,
  [WA_TOOL_COMMANDS.waGetProfilePicUrlTool]: WaGetProfilePicUrlResultSchema,
  [WA_TOOL_COMMANDS.waCreateNewsletterTool]: WaCreateNewsletterResultSchema,
  [WA_TOOL_COMMANDS.waGetNewsletterTool]: WaGetNewsletterResultSchema,
  [WA_TOOL_COMMANDS.getWaMeInfoTool]: GetWaMeInfoResultSchema,
  [WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool]: IsWhatsAppConnectedResultSchema,
  [WA_TOOL_COMMANDS.waReloadPageTool]: WaReloadPageResultSchema,
  [WA_TOOL_COMMANDS.waGuardedReloadTool]: WaGuardedReloadResultSchema,
  [WA_TOOL_COMMANDS.waTriggerLogoutTool]: WaTriggerLogoutResultSchema,
  [WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool]: WaValidatePhoneNumberOrChatIdResultSchema,
  [WA_TOOL_COMMANDS.waGetLinkPreviewTool]: WaGetLinkPreviewResultSchema,
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: WaQueryMessagesResultSchema,
  [WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool]: WaGetChatObjectByChatIdResultSchema,
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: WaQueryContactsAndChatsResultSchema,
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: WaGetLatestChatsAndMessagesResultSchema,
  [WA_TOOL_COMMANDS.waQueryVaultMessagesTool]: WaQueryVaultMessagesResultSchema,
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: WaGetChatParticipantsResultSchema,
  [WA_TOOL_COMMANDS.waGetCommonGroupsTool]: WaGetCommonGroupsResultSchema,
  [WA_TOOL_COMMANDS.waSendSeenTool]: WaSendSeenResultSchema,
  [WA_TOOL_COMMANDS.waOpenChatTool]: WaOpenChatResultSchema,
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: WaLoadMessagesFromPhoneResultSchema,
  [WA_TOOL_COMMANDS.waGetMessageByKeyTool]: WaGetMessageByKeyResultSchema,
  [WA_TOOL_COMMANDS.waGetMessageAckTool]: WaGetMessageAckResultSchema,
  [WA_TOOL_COMMANDS.waGetPollResultsTool]: WaGetPollResultsResultSchema,
  [WA_TOOL_COMMANDS.waGetMessagesAckDataTool]: WaGetMessagesAckDataResultSchema,
  [WA_TOOL_COMMANDS.waGetMessageMediaTool]: WaGetMessageMediaResultSchema,
  [WA_TOOL_COMMANDS.waReactToMessageThunkTool]: WaReactToMessageResultSchema,
  [WA_TOOL_COMMANDS.waForwardMessageTool]: WaForwardMessageResultSchema,
  [WA_TOOL_COMMANDS.waStarMessageTool]: WaStarMessageResultSchema,
  [WA_TOOL_COMMANDS.waDeleteMessageTool]: WaDeleteMessageResultSchema,
  [WA_TOOL_COMMANDS.waPinMessageTool]: WaPinMessageResultSchema,
  [WA_TOOL_COMMANDS.waUnpinMessageTool]: WaUnpinMessageResultSchema,
  [WA_TOOL_COMMANDS.sendMessageThunkTool]: WaSendMessageThunkResultSchema,
  // Chat mutations
  [WA_TOOL_COMMANDS.waClearMessagesTool]: WaClearMessagesResultSchema,
  [WA_TOOL_COMMANDS.waDeleteChatTool]: WaDeleteChatResultSchema,
  [WA_TOOL_COMMANDS.waArchiveChatTool]: WaArchiveChatResultSchema,
  [WA_TOOL_COMMANDS.waUnarchiveChatTool]: WaUnarchiveChatResultSchema,
  [WA_TOOL_COMMANDS.waPinChatTool]: WaPinChatResultSchema,
  [WA_TOOL_COMMANDS.waUnpinChatTool]: WaUnpinChatResultSchema,
  [WA_TOOL_COMMANDS.waMuteChatTool]: WaMuteChatResultSchema,
  [WA_TOOL_COMMANDS.waUnmuteChatTool]: WaUnmuteChatResultSchema,
  [WA_TOOL_COMMANDS.waMarkChatUnreadTool]: WaMarkChatUnreadResultSchema,
  [WA_TOOL_COMMANDS.waSendStateTypingTool]: WaSendStateTypingResultSchema,
  [WA_TOOL_COMMANDS.waSendStateRecordingTool]: WaSendStateRecordingResultSchema,
  [WA_TOOL_COMMANDS.waClearStateTool]: WaClearStateResultSchema,
  // Chat labels / pinned / customer notes
  [WA_TOOL_COMMANDS.waGetChatLabelsTool]: WaGetChatLabelsResultSchema,
  [WA_TOOL_COMMANDS.waChangeLabelsTool]: WaChangeLabelsResultSchema,
  [WA_TOOL_COMMANDS.waGetLabelsTool]: WaGetLabelsResultSchema,
  [WA_TOOL_COMMANDS.waGetPinnedMessagesTool]: WaGetPinnedMessagesResultSchema,
  [WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool]: WaAddOrEditCustomerNoteResultSchema,
  // Labels (extension-only filter)
  [WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool]: WaUpdateShouldAppearChatsByListResultSchema,
  // Session / store validation
  [WA_TOOL_COMMANDS.waValidateStoresTool]: WaValidateStoresResultSchema,
  [WA_TOOL_COMMANDS.waTriggerQRTimeoutTool]: WaTriggerQRTimeoutResultSchema,
  // Status
  [WA_TOOL_COMMANDS.createStatusTool]: CreateStatusResultSchema,
  // Message-store lookup / LID conversion
  [WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE]: GetUnloggedWaMessageKeyIfExistInStoreResultSchema,
  [WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED]: ConvertWaLidSerializedToCusSerializedResultSchema,
  [WA_TOOL_COMMANDS.CONVERT_WA_CUS_SERIALIZED_TO_WA_LID_SERIALIZED]: ConvertWaCusSerializedToWaLidSerializedResultSchema,
  // Utility
  [WA_TOOL_COMMANDS.PING]: PingResultSchema,
  [WA_TOOL_COMMANDS.currentDateTimeTool]: CurrentDateTimeResultSchema,
  // Account health
  [WA_TOOL_COMMANDS.waAccountRestrictionTool]: WaAccountRestrictionResultSchema,
  [WA_TOOL_COMMANDS.waChatSendRiskTool]: WaChatSendRiskResultSchema,
  // Cross-engine scheduling
  [WA_TOOL_COMMANDS.insertOnlyIfMissing]: InsertOnlyIfMissingResultSchema,
} as const satisfies Record<WaToolCommand, z.ZodTypeAny>;

// ── Read limits: the ONLY home of every numeric read default ────────
//
// Absent or `<= 0` → the default; above `max` → `max`; `ALL_ROWS` = no cap.
// There is no other clamp, and no zod `.default()`/`.max()` on the args.
// Message search uses the `waQueryMessagesTool` entry.
export const ALL_ROWS = Number.POSITIVE_INFINITY;

export const WA_READ_LIMITS = {
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: { limit: { default: 50, max: 500 } },
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: { limit: { default: ALL_ROWS } },
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: { limit: { default: ALL_ROWS } },
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: { numberOfChats: { default: 50 }, numberOfMessages: { default: 20 } },
  [WA_TOOL_COMMANDS.waQueryVaultMessagesTool]: { limit: { default: 100 } },
  /** A channel's server-history page (the pod's newsletterFetchMessages); a regular chat's page is the paced fetch's. */
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: { limit: { default: 50 } },
} as const satisfies Partial<Record<WaToolCommand, Record<string, { default: number; max?: number }>>>;

type ReadLimits = typeof WA_READ_LIMITS;

/** The effective value of a numeric read arg (see WA_READ_LIMITS). */
export function resolveLimit<C extends keyof ReadLimits>(
  cmd: C,
  field: keyof ReadLimits[C],
  value: number | null | undefined,
): number {
  const rule = WA_READ_LIMITS[cmd][field] as { default: number; max?: number };
  if (value == null || !(value > 0)) return rule.default;
  return rule.max !== undefined ? Math.min(value, rule.max) : value;
}

// ── Runtime Args-schema lookup ──────────────────────────────────────
//
// Mirror of WA_TOOL_RESULT_SCHEMAS for the *inbound* (engine) side.
// Slice 114b will route every incoming MQTT tool envelope through this
// map to `.safeParse()` the args before handler dispatch — catching
// shape drift at the engine boundary instead of surfacing as
// `data.foo === undefined` reads downstream. Typed as
// `Record<WaToolCommand, z.ZodTypeAny>` so a missing entry trips
// `tsc --noEmit`.

export const WA_TOOL_ARGS_SCHEMAS = {
  // Groups
  [WA_TOOL_COMMANDS.waCreateGroupTool]: WaCreateGroupArgsSchema,
  [WA_TOOL_COMMANDS.waGetGroupDataTool]: WaGetGroupDataArgsSchema,
  [WA_TOOL_COMMANDS.waUpdateGroupNameTool]: WaUpdateGroupNameArgsSchema,
  [WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool]: WaUpdateGroupDescriptionArgsSchema,
  [WA_TOOL_COMMANDS.waAddGroupParticipantTool]: WaAddGroupParticipantArgsSchema,
  [WA_TOOL_COMMANDS.waRemoveGroupParticipantTool]: WaRemoveGroupParticipantArgsSchema,
  [WA_TOOL_COMMANDS.waSetGroupAdminRightsTool]: WaSetGroupAdminRightsArgsSchema,
  [WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool]: WaRemoveGroupAdminRightsArgsSchema,
  [WA_TOOL_COMMANDS.waSetGroupPictureTool]: WaSetGroupPictureArgsSchema,
  [WA_TOOL_COMMANDS.waLeaveGroupTool]: WaLeaveGroupArgsSchema,
  // Contacts
  [WA_TOOL_COMMANDS.waGetProfilePicUrlTool]: WaGetProfilePicUrlArgsSchema,
  // Groups (lean read)
  // Newsletters
  [WA_TOOL_COMMANDS.waCreateNewsletterTool]: WaCreateNewsletterArgsSchema,
  [WA_TOOL_COMMANDS.waGetNewsletterTool]: WaGetNewsletterArgsSchema,
  // Engine / Utils
  [WA_TOOL_COMMANDS.getWaMeInfoTool]: GetWaMeInfoArgsSchema,
  [WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool]: IsWhatsAppConnectedArgsSchema,
  [WA_TOOL_COMMANDS.waReloadPageTool]: WaReloadPageArgsSchema,
  [WA_TOOL_COMMANDS.waGuardedReloadTool]: WaGuardedReloadArgsSchema,
  [WA_TOOL_COMMANDS.waTriggerLogoutTool]: WaTriggerLogoutArgsSchema,
  [WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool]: WaValidatePhoneNumberOrChatIdArgsSchema,
  [WA_TOOL_COMMANDS.waGetLinkPreviewTool]: WaGetLinkPreviewArgsSchema,
  // Chats / Messages
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: WaQueryMessagesArgsSchema,
  [WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool]: WaGetChatObjectByChatIdArgsSchema,
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: WaQueryContactsAndChatsArgsSchema,
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: WaGetLatestChatsAndMessagesArgsSchema,
  [WA_TOOL_COMMANDS.waQueryVaultMessagesTool]: WaQueryVaultMessagesArgsSchema,
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: WaGetChatParticipantsArgsSchema,
  [WA_TOOL_COMMANDS.waGetCommonGroupsTool]: WaGetCommonGroupsArgsSchema,
  [WA_TOOL_COMMANDS.waSendSeenTool]: WaSendSeenArgsSchema,
  [WA_TOOL_COMMANDS.waOpenChatTool]: WaOpenChatArgsSchema,
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: WaLoadMessagesFromPhoneArgsSchema,
  [WA_TOOL_COMMANDS.waGetMessageByKeyTool]: WaGetMessageByKeyArgsSchema,
  [WA_TOOL_COMMANDS.waGetMessageAckTool]: WaGetMessageAckArgsSchema,
  [WA_TOOL_COMMANDS.waGetPollResultsTool]: WaGetPollResultsArgsSchema,
  [WA_TOOL_COMMANDS.waGetMessagesAckDataTool]: WaGetMessagesAckDataArgsSchema,
  [WA_TOOL_COMMANDS.waGetMessageMediaTool]: WaGetMessageMediaArgsSchema,
  [WA_TOOL_COMMANDS.waReactToMessageThunkTool]: WaReactToMessageArgsSchema,
  [WA_TOOL_COMMANDS.waForwardMessageTool]: WaForwardMessageArgsSchema,
  [WA_TOOL_COMMANDS.waStarMessageTool]: WaStarMessageArgsSchema,
  [WA_TOOL_COMMANDS.waDeleteMessageTool]: WaDeleteMessageArgsSchema,
  [WA_TOOL_COMMANDS.waPinMessageTool]: WaPinMessageArgsSchema,
  [WA_TOOL_COMMANDS.waUnpinMessageTool]: WaUnpinMessageArgsSchema,
  [WA_TOOL_COMMANDS.sendMessageThunkTool]: WaSendMessageThunkArgsSchema,
  // Chat mutations
  [WA_TOOL_COMMANDS.waClearMessagesTool]: WaClearMessagesArgsSchema,
  [WA_TOOL_COMMANDS.waDeleteChatTool]: WaDeleteChatArgsSchema,
  [WA_TOOL_COMMANDS.waArchiveChatTool]: WaArchiveChatArgsSchema,
  [WA_TOOL_COMMANDS.waUnarchiveChatTool]: WaUnarchiveChatArgsSchema,
  [WA_TOOL_COMMANDS.waPinChatTool]: WaPinChatArgsSchema,
  [WA_TOOL_COMMANDS.waUnpinChatTool]: WaUnpinChatArgsSchema,
  [WA_TOOL_COMMANDS.waMuteChatTool]: WaMuteChatArgsSchema,
  [WA_TOOL_COMMANDS.waUnmuteChatTool]: WaUnmuteChatArgsSchema,
  [WA_TOOL_COMMANDS.waMarkChatUnreadTool]: WaMarkChatUnreadArgsSchema,
  [WA_TOOL_COMMANDS.waSendStateTypingTool]: WaSendStateTypingArgsSchema,
  [WA_TOOL_COMMANDS.waSendStateRecordingTool]: WaSendStateRecordingArgsSchema,
  [WA_TOOL_COMMANDS.waClearStateTool]: WaClearStateArgsSchema,
  // Chat labels / pinned / customer notes
  [WA_TOOL_COMMANDS.waGetChatLabelsTool]: WaGetChatLabelsArgsSchema,
  [WA_TOOL_COMMANDS.waChangeLabelsTool]: WaChangeLabelsArgsSchema,
  [WA_TOOL_COMMANDS.waGetLabelsTool]: WaGetLabelsArgsSchema,
  [WA_TOOL_COMMANDS.waGetPinnedMessagesTool]: WaGetPinnedMessagesArgsSchema,
  [WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool]: WaAddOrEditCustomerNoteArgsSchema,
  // Labels (extension-only filter)
  [WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool]: WaUpdateShouldAppearChatsByListArgsSchema,
  // Session / store validation
  [WA_TOOL_COMMANDS.waValidateStoresTool]: WaValidateStoresArgsSchema,
  [WA_TOOL_COMMANDS.waTriggerQRTimeoutTool]: WaTriggerQRTimeoutArgsSchema,
  // Status
  [WA_TOOL_COMMANDS.createStatusTool]: CreateStatusArgsSchema,
  // Message-store lookup / LID conversion
  [WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE]: GetUnloggedWaMessageKeyIfExistInStoreArgsSchema,
  [WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED]: ConvertWaLidSerializedToCusSerializedArgsSchema,
  [WA_TOOL_COMMANDS.CONVERT_WA_CUS_SERIALIZED_TO_WA_LID_SERIALIZED]: ConvertWaCusSerializedToWaLidSerializedArgsSchema,
  // Utility
  [WA_TOOL_COMMANDS.PING]: PingArgsSchema,
  [WA_TOOL_COMMANDS.currentDateTimeTool]: CurrentDateTimeArgsSchema,
  // Cross-engine scheduling
  [WA_TOOL_COMMANDS.waAccountRestrictionTool]: WaAccountRestrictionArgsSchema,
  [WA_TOOL_COMMANDS.waChatSendRiskTool]: WaChatSendRiskArgsSchema,
  [WA_TOOL_COMMANDS.insertOnlyIfMissing]: InsertOnlyIfMissingArgsSchema,
} as const satisfies Record<WaToolCommand, z.ZodTypeAny>;

// ── Contract version ─────────────────────────────────────────────────
//
// Bumped whenever an engine-visible contract rule changes (a §7 gate item).
// Engines publish it as `EnginePresence.contractVersion`; the backend's 5.0
// dispatch gate selects only engines at or above the required version.
export const WIRE_CONTRACT_VERSION = 1;

// ── State-changing commands (engine-side command dedup scope) ───────
//
// Commands that mutate WhatsApp state: a duplicate delivery would be a second
// visible effect, so only these take the remote `_engineCmdId` lock (the pod
// router). A retried read never does. Moved as is from FE
// `common/noifier-module/engine-cmd-gate.ts` (which imports it from here).
//
// DEDUP SAFETY INVARIANT: the lock is safe ONLY while _engineCmdId is minted 1:1
// per requestId with no shared `opts.lockId` fan-out — distinct logical commands
// always carry distinct _engineCmdIds.
export const STATE_CHANGING_ENGINE_COMMANDS: ReadonlySet<string> = new Set<string>([
  // send / forward (duplicate = a second delivered message)
  WA_TOOL_COMMANDS.sendMessageThunkTool,
  WA_TOOL_COMMANDS.waForwardMessageTool,
  // delete
  WA_TOOL_COMMANDS.waDeleteMessageTool,
  // status post (duplicate = a second status)
  WA_TOOL_COMMANDS.createStatusTool,
  // reaction (duplicate = a duplicate/toggled reaction)
  WA_TOOL_COMMANDS.waReactToMessageThunkTool,
  // group / newsletter mutations
  WA_TOOL_COMMANDS.waCreateGroupTool,
  WA_TOOL_COMMANDS.waUpdateGroupNameTool,
  WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool,
  WA_TOOL_COMMANDS.waAddGroupParticipantTool,
  WA_TOOL_COMMANDS.waRemoveGroupParticipantTool,
  WA_TOOL_COMMANDS.waSetGroupAdminRightsTool,
  WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool,
  WA_TOOL_COMMANDS.waSetGroupPictureTool,
  WA_TOOL_COMMANDS.waLeaveGroupTool,
  WA_TOOL_COMMANDS.waCreateNewsletterTool,
]);

/** True iff `command` mutates WhatsApp state and therefore needs the remote lock. */
export function isStateChangingEngineCommand(command: string | null | undefined): boolean {
  return !!command && STATE_CHANGING_ENGINE_COMMANDS.has(command);
}

// ── Per-engine command support manifests ─────────────────────────────
//
// A manifest = what the engine answers ON THE WIRE: the keys of its ONE wire
// dispatch literal, tsc-bound to it both ways (a missing handler is a compile
// error; a handler for an undeclared command fails the excess-property
// check). What an engine does not handle is simply WaToolCommand minus its
// manifest. The Chrome manifest is bound to the extension's content-notifier
// dispatch (CHROME_WIRE_HANDLERS, wa-tools-notifier-handlers.ts) — the page
// handler map is typed as the subset of it the page serves.
//
// `satisfies Partial<Record<WaToolCommand, true>>` constrains keys to declared
// contract commands (typos fail tsc) while keeping the literal's keys.

export const CHROME_SUPPORTED_COMMANDS = {
  [WA_TOOL_COMMANDS.waCreateGroupTool]: true,
  [WA_TOOL_COMMANDS.waGetGroupDataTool]: true,
  [WA_TOOL_COMMANDS.waUpdateGroupNameTool]: true,
  [WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool]: true,
  [WA_TOOL_COMMANDS.waAddGroupParticipantTool]: true,
  [WA_TOOL_COMMANDS.waRemoveGroupParticipantTool]: true,
  [WA_TOOL_COMMANDS.waSetGroupAdminRightsTool]: true,
  [WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool]: true,
  [WA_TOOL_COMMANDS.waSetGroupPictureTool]: true,
  [WA_TOOL_COMMANDS.waLeaveGroupTool]: true,
  [WA_TOOL_COMMANDS.waGetProfilePicUrlTool]: true,
  [WA_TOOL_COMMANDS.waCreateNewsletterTool]: true,
  [WA_TOOL_COMMANDS.waGetNewsletterTool]: true,
  [WA_TOOL_COMMANDS.getWaMeInfoTool]: true,
  [WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool]: true,
  [WA_TOOL_COMMANDS.waGuardedReloadTool]: true,
  [WA_TOOL_COMMANDS.waReloadPageTool]: true,
  [WA_TOOL_COMMANDS.waTriggerLogoutTool]: true,
  [WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool]: true,
  [WA_TOOL_COMMANDS.waGetLinkPreviewTool]: true,
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: true,
  [WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool]: true,
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: true,
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: true,
  [WA_TOOL_COMMANDS.waQueryVaultMessagesTool]: true,
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: true,
  [WA_TOOL_COMMANDS.waGetCommonGroupsTool]: true,
  [WA_TOOL_COMMANDS.waSendSeenTool]: true,
  [WA_TOOL_COMMANDS.waOpenChatTool]: true,
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: true,
  [WA_TOOL_COMMANDS.waGetMessageByKeyTool]: true,
  [WA_TOOL_COMMANDS.waGetMessageAckTool]: true,
  [WA_TOOL_COMMANDS.waGetPollResultsTool]: true,
  [WA_TOOL_COMMANDS.waGetMessagesAckDataTool]: true,
  [WA_TOOL_COMMANDS.waReactToMessageThunkTool]: true,
  [WA_TOOL_COMMANDS.waForwardMessageTool]: true,
  [WA_TOOL_COMMANDS.waStarMessageTool]: true,
  [WA_TOOL_COMMANDS.waDeleteMessageTool]: true,
  [WA_TOOL_COMMANDS.waPinMessageTool]: true,
  [WA_TOOL_COMMANDS.waUnpinMessageTool]: true,
  [WA_TOOL_COMMANDS.sendMessageThunkTool]: true,
  // Re-added now that the extension/Chrome handlers exist in PageMessageService
  // (landed via prod). (Temporarily removed in 5683309 while the FE handlers
  // were unimplemented.)
  [WA_TOOL_COMMANDS.waAccountRestrictionTool]: true,
  [WA_TOOL_COMMANDS.waChatSendRiskTool]: true,
  // Answered content-side or by page handlers registered outside the old
  // page literal — part of the wire set since plan 3.11.
  [WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED]: true,
  [WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE]: true,
  [WA_TOOL_COMMANDS.PING]: true,
  [WA_TOOL_COMMANDS.createStatusTool]: true,
  [WA_TOOL_COMMANDS.currentDateTimeTool]: true,
  [WA_TOOL_COMMANDS.insertOnlyIfMissing]: true,
  [WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool]: true,
  [WA_TOOL_COMMANDS.waArchiveChatTool]: true,
  [WA_TOOL_COMMANDS.waChangeLabelsTool]: true,
  [WA_TOOL_COMMANDS.waClearMessagesTool]: true,
  [WA_TOOL_COMMANDS.waClearStateTool]: true,
  [WA_TOOL_COMMANDS.waDeleteChatTool]: true,
  [WA_TOOL_COMMANDS.waGetChatLabelsTool]: true,
  [WA_TOOL_COMMANDS.waGetLabelsTool]: true,
  [WA_TOOL_COMMANDS.waGetMessageMediaTool]: true,
  [WA_TOOL_COMMANDS.waGetPinnedMessagesTool]: true,
  [WA_TOOL_COMMANDS.waMarkChatUnreadTool]: true,
  [WA_TOOL_COMMANDS.waMuteChatTool]: true,
  [WA_TOOL_COMMANDS.waPinChatTool]: true,
  [WA_TOOL_COMMANDS.waSendStateRecordingTool]: true,
  [WA_TOOL_COMMANDS.waSendStateTypingTool]: true,
  [WA_TOOL_COMMANDS.waTriggerQRTimeoutTool]: true,
  [WA_TOOL_COMMANDS.waUnarchiveChatTool]: true,
  [WA_TOOL_COMMANDS.waUnmuteChatTool]: true,
  [WA_TOOL_COMMANDS.waUnpinChatTool]: true,
  [WA_TOOL_COMMANDS.waValidateStoresTool]: true,
} as const satisfies Partial<Record<WaToolCommand, true>>;

export type ChromeCommand = keyof typeof CHROME_SUPPORTED_COMMANDS;

/**
 * The Baileys pod's capability manifest: exactly the contract commands its
 * router registers. Enforced by the pod declaring its handlers as ONE object
 * literal annotated `EngineHandlerMap<typeof BAILEYS_SUPPORTED_COMMANDS>`
 * (mapped required keys + excess-property checks = tsc checks both ways).
 * A command absent here is a declared gap; the router answers UNKNOWN_COMMAND.
 */
export const BAILEYS_SUPPORTED_COMMANDS = {
  [WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED]: true,
  [WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE]: true,
  [WA_TOOL_COMMANDS.PING]: true,
  [WA_TOOL_COMMANDS.createStatusTool]: true,
  [WA_TOOL_COMMANDS.currentDateTimeTool]: true,
  [WA_TOOL_COMMANDS.getWaMeInfoTool]: true,
  [WA_TOOL_COMMANDS.insertOnlyIfMissing]: true,
  [WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool]: true,
  [WA_TOOL_COMMANDS.sendMessageThunkTool]: true,
  [WA_TOOL_COMMANDS.waAccountRestrictionTool]: true,
  [WA_TOOL_COMMANDS.waAddGroupParticipantTool]: true,
  [WA_TOOL_COMMANDS.waArchiveChatTool]: true,
  [WA_TOOL_COMMANDS.waChangeLabelsTool]: true,
  [WA_TOOL_COMMANDS.waChatSendRiskTool]: true,
  [WA_TOOL_COMMANDS.waClearMessagesTool]: true,
  [WA_TOOL_COMMANDS.waClearStateTool]: true,
  [WA_TOOL_COMMANDS.waCreateGroupTool]: true,
  [WA_TOOL_COMMANDS.waCreateNewsletterTool]: true,
  [WA_TOOL_COMMANDS.waDeleteChatTool]: true,
  [WA_TOOL_COMMANDS.waDeleteMessageTool]: true,
  [WA_TOOL_COMMANDS.waForwardMessageTool]: true,
  [WA_TOOL_COMMANDS.waGetChatLabelsTool]: true,
  [WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool]: true,
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: true,
  [WA_TOOL_COMMANDS.waGetCommonGroupsTool]: true,
  [WA_TOOL_COMMANDS.waGetGroupDataTool]: true,
  [WA_TOOL_COMMANDS.waGetLabelsTool]: true,
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: true,
  [WA_TOOL_COMMANDS.waGetLinkPreviewTool]: true,
  [WA_TOOL_COMMANDS.waGetMessageAckTool]: true,
  [WA_TOOL_COMMANDS.waGetPollResultsTool]: true,
  [WA_TOOL_COMMANDS.waGetMessageByKeyTool]: true,
  [WA_TOOL_COMMANDS.waGetMessageMediaTool]: true,
  [WA_TOOL_COMMANDS.waGetMessagesAckDataTool]: true,
  [WA_TOOL_COMMANDS.waGetNewsletterTool]: true,
  [WA_TOOL_COMMANDS.waGetPinnedMessagesTool]: true,
  [WA_TOOL_COMMANDS.waGetProfilePicUrlTool]: true,
  [WA_TOOL_COMMANDS.waLeaveGroupTool]: true,
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: true,
  [WA_TOOL_COMMANDS.waMarkChatUnreadTool]: true,
  [WA_TOOL_COMMANDS.waMuteChatTool]: true,
  [WA_TOOL_COMMANDS.waOpenChatTool]: true,
  [WA_TOOL_COMMANDS.waPinChatTool]: true,
  [WA_TOOL_COMMANDS.waPinMessageTool]: true,
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: true,
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: true,
  [WA_TOOL_COMMANDS.waReactToMessageThunkTool]: true,
  [WA_TOOL_COMMANDS.waReloadPageTool]: true,
  [WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool]: true,
  [WA_TOOL_COMMANDS.waRemoveGroupParticipantTool]: true,
  [WA_TOOL_COMMANDS.waSendSeenTool]: true,
  [WA_TOOL_COMMANDS.waSendStateRecordingTool]: true,
  [WA_TOOL_COMMANDS.waSendStateTypingTool]: true,
  [WA_TOOL_COMMANDS.waSetGroupAdminRightsTool]: true,
  [WA_TOOL_COMMANDS.waSetGroupPictureTool]: true,
  [WA_TOOL_COMMANDS.waStarMessageTool]: true,
  [WA_TOOL_COMMANDS.waTriggerLogoutTool]: true,
  [WA_TOOL_COMMANDS.waUnarchiveChatTool]: true,
  [WA_TOOL_COMMANDS.waUnmuteChatTool]: true,
  [WA_TOOL_COMMANDS.waUnpinChatTool]: true,
  [WA_TOOL_COMMANDS.waUnpinMessageTool]: true,
  [WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool]: true,
  [WA_TOOL_COMMANDS.waUpdateGroupNameTool]: true,
  [WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool]: true,
} as const satisfies Partial<Record<WaToolCommand, true>>;

/**
 * The one engine handler signature, shared by both engines: contract Args in,
 * contract Result out; failure = `throw new EngineToolError(code, message)`.
 * The pod router wraps `{success, data}`; the extension page handler site
 * forwards `errorType: error.code ?? error.name`.
 */
export type EngineHandler<C extends WaToolCommand> = (args: WaToolArgs<C>) => Promise<WaToolResult<C>>;

/** Every command in manifest `M` has exactly one EngineHandler. Each engine
 *  writes `EngineHandlerMap<typeof X_SUPPORTED_COMMANDS>` once, at its single
 *  handler-map literal. */
export type EngineHandlerMap<M extends Partial<Record<WaToolCommand, true>>> = {
  [C in keyof M & WaToolCommand]: EngineHandler<C>;
};
